/**
 * Trace Sandbox
 * Advanced trace viewer editor with Babylon.js Inspector v2 integration
 */

// MUST be first import - configures Babylon.js to use embedded shaders
import './shader-config';
import { Assets } from 'trace-viewer/cms';
import { assetContentSnapshot, assetExportData } from './cms/asset-content-snapshot';
import { adoptDocumentRoot, fileNameStem, renameDefaultDocumentRoot } from './cms/document-root';
import { installDocumentRootSceneTree, reportSceneTreeReroot } from './scene-tree-service';

import 'trace-viewer';
import type { SceneObject, TraceViewerElement } from 'trace-viewer';
import { DROPPABLE_ASSET_EXTENSIONS } from 'trace-viewer/scene';
import { partitionSiblingFiles } from './sibling-files';

import { ShowInspector } from '@babylonjs/inspector';
import type { InspectorOptions } from '@babylonjs/inspector';
import type { IDisposable } from '@babylonjs/core/scene';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { GridMaterial } from '@babylonjs/materials/grid/gridMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Tools } from '@babylonjs/core/Misc/tools';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TraceViewerServiceDefinition, settingsStore, applyStoredSettings, getShellService } from './inspector-extension';
import { setViewMode, type ViewMode } from './view-mode';
import { BehaviorsPanelServiceDefinition } from './behaviors-panel';
import { DesignPanelServiceDefinition } from './design-panel';
import { TraceExportServiceDefinition } from './trace-export-extension';
import { GizmoKeyboardServiceDefinition, setGizmoKeyboardTraceScene } from './gizmo-keyboard-service';
import { PlanarGizmoServiceDefinition } from './planar-gizmo-service';
import { SelectionBridgeServiceDefinition, getSelectionService, suppressSelectionBridge } from './selection-bridge-service';
import type { BehaviorData } from 'trace-viewer/behaviors';
import { Pages, ParticleSystem, Renderer } from 'trace-viewer/behaviors';
import { createCmsManagerFromUrl, CmsManager, buildPublishSummary, type PublishSceneOptions, type RootContentFormat } from './cms/CmsManager';
import { setExportCmsManager } from './trace-export-extension';
// Eager auth: triggers token refresh on import so user is logged in before catalog panel opens
import './catalog-auth';
import { getCatalogToken, apiConfig, clearSourceAsset, setSourceAsset, getSourceAsset, sceneHasContent, API_BASE_URL, BLOB_BASE_URL, type SourceAsset } from './catalog-shared';
import { setActiveCmsManager } from './cms/active-manager';
import { copyForInsideEdit, needsReferenceCopy } from './cms/reference-edit-guard';
import { importDroppedFile, type ImportedFile } from './drop-import';
import { EDITOR_SCENE_EXTENSIONS, bundleFileName } from './scene-formats';
import { assetManifestOptions } from './cms/scene-folder';
import { openAssetFolderSession, publishAssetFolder, resetAssetFolderSession } from './cms/asset-folder';
import { resolveCodeParam } from './code-loader';
import { snapshotExportData, resolvePublishDecision, EditorSession, captureSceneThumbnail } from 'trace-viewer/editor';
import { ensureSharedGroup, documentRoot, isEngineRoot, isStructuralRoot, structuralRootRefusal, topLevelObjects, topLevelParent } from 'trace-viewer/editor';
import { deletePage, isSharedIntent, pageChildrenOf, resolvePageContext } from 'trace-viewer/editor';
import { getUndoManager, createFromTemplate, duplicateObjects, installReferenceIdentity, installAutoSeparateOnGroupEntry, enableTriplanarPreview, type ObjectTemplate } from 'trace-viewer/editor';
import { createAudioGizmoController, type AudioGizmoController } from 'trace-viewer/editor';
import { createTimelineWidget } from './timeline-widget';
import { observeInspectorPaneInsets } from './inspector-panes';
import { abandonPlayMode, enterPlayMode, exitPlayMode } from './PlayModeSnapshot';
import { UndoGizmoServiceDefinition } from './undo-gizmo-service';
import { BoundingBoxGizmoServiceDefinition } from './bounding-box-gizmo-service';
import { LayoutGizmoServiceDefinition } from './layout-gizmo-service';
import { PlanarDragServiceDefinition } from './planar-drag-service';
import { MultiSelectionService } from './multi-selection-service';
import { GroupTransformService } from './group-transform-service';
import { topMostSelected } from './selection-utils';
import { buildDeleteCommand } from './undo-capture';
import { setActiveSelectionSource } from './active-selection';
import { PlanarDragToolbarDefinition } from './planar-drag-toolbar';
import { CatalogPanelServiceDefinition } from './catalog-panel';
import { ResourcesPanelServiceDefinition } from './resources-panel';
import { EDITOR_EVENTS, dispatchBehaviorChanged, dispatchResourcesChanged } from './editor-events';
import type { BehaviorChangedDetail } from './editor-events';
import {
    CreateObjectCommand,
    type CreateChildEntry,
    CompositeCommand,
    type UndoCommand,
    pruneEmptyRig,
} from 'trace-viewer/editor';
import { showSpinnerToast, showToast } from './toast';
import { downloadBlob } from './utils/download-blob';
import { mountChatPanel, type ChatPanelControls } from './ai/mount-chat';
import { mountXRAssistant, type XRAssistantControls } from './ai/mount-xr-assistant';
import { GizmoSnapServiceDefinition } from './gizmo-snap-service';
import { mountClipEditor, type ClipEditorControls } from './mount-clip-editor';
import { ReplayControls } from './replay-controls';
import { mountPageContext, type PageContextControls } from './mount-page-context';
import { mountKeyframeContext, resolveKeyframeContext, type KeyframeContextControls } from './mount-keyframe-context';
import { stripForScene, resolveContext, type StateContextControls } from './mount-state-context';
import { installSetValueThumbnailBridge } from './setvalue-thumbnail-bridge';
import { registerSceneSaver, sessionDirtyProbe } from './save-bridge';
import { acknowledgeCatalogImport, type CatalogImportDetail } from './catalog-import';
import { installAutoUISwatchEditorCommit } from './autoui-swatch-editor-commit';
import { resolveStateParent, resolveNewObjectParent, resolveImportParent } from './asset-parent-target';
import { isStatesEditingEnabled } from './states-editing-mode';
import { initKeyframePropertyCapture } from './keyframe-capture';
import { toggleActive } from './state-capture';
import { setPageEditingEnabled } from './page-editing-mode';
import { pushOp } from './behaviors/pagesSharedActions';
import { getStripTabs } from './strip-tabs';
import { configureStateActions, ensureStateContextContainer, setStateDiagnostics } from 'trace-viewer/editor';
import { Animation } from 'trace-viewer/behaviors';
import { extractKeyframesFromAnimationGroup } from 'trace-viewer/animation';
import { ExportContextMenuServiceDefinition } from './export-context-menu-service';
import { InferenceContextMenuServiceDefinition } from './inference-context-menu-service';
import { setupViewportContextMenu } from './viewport-context-menu';
import { setupViewportTextEditor } from './viewport-text-editor';
import { initHostFontPacks } from './host-font-packs';
import { editorExclusion, exportGlz, setIncludeEditorContentInDownloads } from './editor-content';
import { restoreEditorContent } from './ai/session-restore';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

/** A data file entry - either a simple path or a { src, behaviors } object */
type DataFileEntry = string | { src: string; behaviors?: BehaviorData[] };

/** Shape of grid-related data stored in Babylon's reservedDataStore (typed as `any`). */
interface GridDataStore { gridMesh?: Mesh | null; isInspectorGrid?: boolean }

/** Fixed editor grid extent (meters). Sized to comfortably cover room-scale AR scenes. */
const GRID_SIZE_M = 100;

/** Cast Babylon's untyped reservedDataStore to our grid shape. */
function gridStore(obj: { reservedDataStore: unknown }): GridDataStore {
    const store = obj.reservedDataStore as GridDataStore | undefined;
    if (store) return store;
    const created: GridDataStore = {};
    obj.reservedDataStore = created;
    return created;
}

interface DataConfigFile {
    dataFiles: DataFileEntry[];
}

/** Detect mobile/narrow viewports where a single-pane overlay layout is preferred. */
function isMobile(): boolean {
    return window.innerWidth < 768;
}

class TraceSandbox {
    private viewer: TraceViewerElement | null = null;
    private inspectorVisible: boolean = false;
    private inspectorDisposable: IDisposable | null = null;
    // Store object reference instead of name to handle duplicate names correctly
    private selectedObject: SceneObject | null = null;
    // Editor multi-object selection (set + primary). `selectedObject` above
    // remains the primary; this owns the full set for batch operations.
    private multiSelection: MultiSelectionService | null = null;
    // Group transform (centroid pivot) — active when >1 object is selected.
    private groupTransform: GroupTransformService | null = null;
    private dataConfig: DataConfigFile | null = null;

    // CMS backend integration (null = standalone mode)
    private cmsManager: CmsManager | null = null;

    // Publish session state (source asset, dirty tracking, content snapshots)
    private _editorSession = new EditorSession(() => getUndoManager().undoDepth);

    // AI chat panel
    private chatPanelControls: ChatPanelControls | null = null;
    // XR voice assistant
    private xrAssistantControls: XRAssistantControls | null = null;

    // Clip editor overlay
    private clipEditor: ClipEditorControls | null = null;

    // Page context overlay
    private pageContext: PageContextControls | null = null;
    // Keyframe context overlay
    private keyframeContext: KeyframeContextControls | null = null;
    private stateContext: StateContextControls | null = null;
    // Routes Properties commits into the selected keyframe (keyframe-capture.ts)
    private keyframePropertyCaptureDispose: (() => void) | null = null;

    // UI Elements
    private dataFilesSelector: HTMLSelectElement;
    private togglePlayModeBtn: HTMLButtonElement;
    private toggleGridBtn: HTMLButtonElement;
    private newObjectDropdown: HTMLDivElement;
    private deleteObjectBtn: HTMLButtonElement;

    private exportDropdown: HTMLDivElement;
    private viewerWrapper: HTMLDivElement;
    private emptySceneOverlay: HTMLDivElement;
    private filePicker: HTMLInputElement;
    // Whether the next file-picker selection imports into the scene or opens
    // as a new scene (replacing current content)
    private filePickerMode: 'import' | 'open' = 'import';

    // Play mode state snapshot for restore-on-stop
    // Editor keyframe selection captured on play, restored on stop. Play shows
    // the authored start (initialKeyframe / Base) like a deployed scene would;
    // stop returns the user to whatever keyframe they were editing.

    // Editor-only marker for AudioClip SceneObjects (invisible behavior needs
    // a picker mesh). Created once per viewer instance.
    private audioGizmoController: AudioGizmoController | null = null;

    // Grid state
    private gridEnabled: boolean = true;

    // F-key focus: double-tap toggles between tight and wide framing
    private lastFocusTime: number = 0;
    private focusIsWide: boolean = false;
    private _gridWasEnabled: boolean = true;

    // Safe area tracking for inspector panels
    private _disposeSafeAreaTracking: (() => void) | null = null;

    // Record/replay controls (top-bar record + load&play buttons)
    private replayControls: ReplayControls | null = null;

    constructor() {
        // Initialize UI elements
        this.dataFilesSelector = document.getElementById('data-files-selector') as HTMLSelectElement;
        this.togglePlayModeBtn = document.getElementById('toggle-play-mode') as HTMLButtonElement;
        this.toggleGridBtn = document.getElementById('toggle-grid') as HTMLButtonElement;
        this.newObjectDropdown = document.getElementById('new-object-dropdown') as HTMLDivElement;
        this.deleteObjectBtn = document.getElementById('delete-object') as HTMLButtonElement;

        this.exportDropdown = document.getElementById('export-dropdown') as HTMLDivElement;
        this.viewerWrapper = document.getElementById('viewer-wrapper') as HTMLDivElement;
        this.emptySceneOverlay = document.getElementById('empty-scene-overlay') as HTMLDivElement;
        this.filePicker = document.getElementById('file-picker') as HTMLInputElement;

        this.replayControls = new ReplayControls(() => this.viewer);

        void this.init();
    }

    private async init(): Promise<void> {
        console.log('[Sandbox] Initializing...');

        await this.loadDataConfig();
        this.setupEventListeners();
        this.setupDragAndDrop();
        // Let the headless `save_scene` tool persist runtime edits (which are
        // otherwise lost on reload) via the same publish/PATCH route the UI uses.
        // Registered here, not per scene load: the empty scene a chat build
        // starts in is never "loaded", and saveForAgent reads the current viewer.
        registerSceneSaver(
            () => this.saveForAgent(),
            sessionDirtyProbe(this._editorSession),
        );

        // Create an empty viewer on page load
        await this.createEmptyViewer();

        // ?code=XXXX → resolve to a scene id and rewrite URL to the ?scene=&api= form.
        // Uses logged-in session if available, else guest login.
        await resolveCodeParam();

        // Parse CMS scene-targeting params (?scene=&api= or CMS URL in ?src=).
        // Token comes from the catalog-auth refresh-token cookie, not the URL.
        this.cmsManager = createCmsManagerFromUrl(window.location.search, getCatalogToken);

        if (this.cmsManager) {
            // CMS mode: load scene from backend API
            const cms = this.cmsManager;
            setExportCmsManager(cms);
            setActiveCmsManager(cms);
            this.emptySceneOverlay.classList.remove('visible');
            console.log(`[Sandbox] CMS mode: loading scene ${cms.sceneId} from ${cms.client.apiBaseUrl}`);
            await cms.loadScene(this.viewer!, () => this.onSceneLoaded());
            // Re-establish CMS connection after onSceneLoaded() disconnected the previous state
            this.cmsManager = cms;
            setExportCmsManager(cms);
            setActiveCmsManager(cms);
            // Track CMS publish state via EditorSession (sceneId as assetId)
            this._editorSession.open({ assetId: cms.sceneId }, null);
            // Gate Publish UI on a usable Root state: either the Root row
            // loaded, or there's no Root row yet (new scene — first publish
            // creates it). The only refused case is a Root row that existed
            // but failed to load: publishing would overwrite the broken Root
            // with the editor's empty/placeholder state.
            if (cms.rootLoaded || cms.rootMissing) {
                this.showPublishButtons('cms-scene');
                this.setupCmsStatusDot();
            } else {
                console.warn('[Sandbox] CMS Scene loaded with broken RootContent — Publish disabled');
            }
        } else {
            // Standalone mode: check for regular ?src= parameter
            const srcParam = new URLSearchParams(window.location.search).get('src');
            if (srcParam) {
                this.emptySceneOverlay.classList.remove('visible');
                console.log(`[Sandbox] Loading from URL parameter: ${srcParam}`);
                await this.viewer!.open({ src: srcParam });
                // Every session has a document root (D8): the reader gives a
                // document one, and a legacy flat archive gets one here.
                await adoptDocumentRoot(this.viewer!.traceScene, null);
                this.onSceneLoaded();
            }
        }

        console.log('[Sandbox] Ready - drag and drop files to load');
    }

    private async loadDataConfig(): Promise<void> {
        try {
            const response = await fetch('/test.config.local.json');
            // The file is gitignored and served only when present; absent, the
            // SPA fallback answers 200 with index.html, so `ok` alone let
            // `.json()` throw on every load (#74). Presence is the content type.
            const isJson = response.headers.get('content-type')?.includes('json') ?? false;
            if (response.ok && isJson) {
                this.dataConfig = await response.json() as typeof this.dataConfig;
                this.populateDataFiles();
            } else {
                console.log('[Sandbox] No local data config found');
            }
        } catch (error) {
            console.log('[Sandbox] Could not load data config:', error);
        }
    }

    private populateDataFiles(): void {
        if (!this.dataConfig?.dataFiles || this.dataConfig.dataFiles.length === 0) {
            this.dataFilesSelector.style.display = 'none';
            return;
        }

        this.dataFilesSelector.style.display = '';
        this.dataFilesSelector.innerHTML = '';

        // Add empty "Select asset" option first
        const emptyOption = document.createElement('option');
        emptyOption.value = '';
        emptyOption.textContent = 'Select asset...';
        this.dataFilesSelector.appendChild(emptyOption);

        this.dataConfig.dataFiles.forEach(entry => {
            const option = document.createElement('option');
            // Normalize to { src, behaviors? } format
            const normalized = typeof entry === 'string' ? { src: entry } : entry;
            option.value = JSON.stringify(normalized);
            // Display just the src path, with indicator if behaviors are attached
            const label = normalized.behaviors?.length
                ? `${normalized.src} (+${normalized.behaviors.length} behaviors)`
                : normalized.src;
            option.textContent = label;
            this.dataFilesSelector.appendChild(option);
        });

        console.log(`[Sandbox] Loaded ${this.dataConfig.dataFiles.length} data files`);
    }

    private setupEventListeners(): void {
        // Load scene automatically when selection changes
        this.dataFilesSelector.addEventListener('change', () => void this.loadScene());
        this.togglePlayModeBtn.addEventListener('click', () => void this.togglePlayMode());
        this.toggleGridBtn.addEventListener('click', () => this.toggleGrid());
        document.getElementById('toggle-ai')?.addEventListener('click', () => this.chatPanelControls?.toggle());
        this.setupNewObjectDropdown();
        this.deleteObjectBtn.addEventListener('click', () => this.deleteSelectedObject());

        this.setupExportDropdown();
        this.setupViewModeDropdown();

        // Listen for selection changes to update delete button state.
        // The primary may be re-pointed mid-reconcile by the multi-selection
        // model (e.g. ctrl-clicking the primary off), so when that model is
        // active it is the authority (via onPrimaryChanged, wired on creation).
        // This raw-event path is the fallback before the model exists.
        window.addEventListener('trace-viewer:selection-changed', (event: Event) => {
            if (this.multiSelection) return; // model drives applySelectedObject()
            const detail = (event as CustomEvent<{ object?: SceneObject | null }>).detail;
            this.applySelectedObject(detail?.object ?? null);
        });

        // Adding/removing a Keyframes (or Properties) behavior on the
        // already-selected object doesn't fire a selection change, so the
        // overlays wouldn't appear (or disappear) until reselection. Refresh on
        // behavior add/remove so the relevant strip shows up immediately.
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, (event: Event) => {
            const detail = (event as CustomEvent<BehaviorChangedDetail>).detail;
            if (detail?.behaviorType === 'Keyframes' && detail.action !== 'updated') {
                this.keyframeContext?.update(this.selectedObject);
            }
            if (detail?.behaviorType === 'Properties' && detail.action !== 'updated') {
                // Adding/removing Properties on the already-selected object doesn't
                // change the resolved owner, so update()'s diff would skip it; but
                // the strip's lazy `thumbnails` getter + 300ms poll pick up the new
                // provider on their own. update() still covers an owner change.
                this.stateContext?.update(this.selectedObject);
            }
            if (detail?.behaviorType === 'Pages' && detail.action !== 'updated') {
                this.pageContext?.update(this.selectedObject);
                // Adding Pages TO THE SELECTION (behaviors-panel "Add
                // Behavior") is the same gesture as the pages template —
                // open the tab so the pages context is immediately visible.
                // Gated on the selection so load-time hydration can't trigger.
                if (detail.action === 'added' && this.selectedObject
                    && detail.objectPath === this.selectedObject.getPath()) {
                    getStripTabs().setOpen('pages');
                    setPageEditingEnabled(true);
                }
            }
        });

        // Parenting into an imported-scene subtree is vetoed by trace-viewer
        // (children there are derived and would be dropped on export) —
        // surface the reason, since hierarchy drag-drop only reverts silently.
        window.addEventListener('trace-viewer:reparent-vetoed', (event: Event) => {
            const detail = (event as CustomEvent<{ message?: string }>).detail;
            showToast(detail?.message ?? 'Cannot add children to an imported scene.', 'error');
        });

        // Listen for "Create Clip" requests from the Animation behavior inspector
        window.addEventListener(EDITOR_EVENTS.CREATE_CLIP, () => {
            const traceScene = this.viewer?.traceScene;
            if (this.selectedObject && traceScene) {
                const anim = this.selectedObject.getBehavior('Animation');
                const existingClipNames = anim?.getClipNames() ?? [];
                this.clipEditor?.open(
                    this.selectedObject,
                    traceScene.getBabylonScene(),
                    traceScene.resources,
                    { existingClipNames },
                );
            }
        });

        // Listen for "Edit Clip" requests — load existing keyframes from the GLB
        window.addEventListener(EDITOR_EVENTS.EDIT_CLIP, () => {
            const traceScene = this.viewer?.traceScene;
            if (!this.selectedObject || !traceScene) return;

            const anim = this.selectedObject.getBehavior('Animation');
            const clipName = anim?.getData()?.clip;
            if (!anim || !clipName) return;

            const group = anim.getLoadedGroup(clipName);
            if (group) {
                const keyframes = extractKeyframesFromAnimationGroup(group);
                this.clipEditor?.open(
                    this.selectedObject,
                    traceScene.getBabylonScene(),
                    traceScene.resources,
                    { initialKeyframes: keyframes, initialClipName: clipName },
                );
            } else {
                // Clip not loaded yet — open empty editor with just the name
                this.clipEditor?.open(
                    this.selectedObject,
                    traceScene.getBabylonScene(),
                    traceScene.resources,
                    { initialClipName: clipName },
                );
            }
        });

        // Listen for clip selection changes — update clip editor if active
        window.addEventListener(EDITOR_EVENTS.CLIP_SELECTED, (event: Event) => {
            if (!this.clipEditor?.isActive()) return;
            const traceScene = this.viewer?.traceScene;
            if (!this.selectedObject || !traceScene) return;

            const { clipName } = (event as CustomEvent<{ clipName: string }>).detail;
            const anim = this.selectedObject.getBehavior('Animation');
            if (!anim) return;

            // Close clip editor when switching to an embedded clip
            // (clip editor only supports external/custom clips)
            const embeddedNames = new Set(anim.getEmbeddedClipNames());
            if (embeddedNames.has(clipName)) {
                this.clipEditor.close();
                return;
            }

            const group = anim.getLoadedGroup(clipName);
            const keyframes = group ? extractKeyframesFromAnimationGroup(group) : [];
            this.clipEditor.open(
                this.selectedObject,
                traceScene.getBabylonScene(),
                traceScene.resources,
                {
                    initialKeyframes: keyframes,
                    initialClipName: clipName,
                    existingClipNames: anim.getClipNames(),
                },
            );
        });

        // Close clip editor on request (e.g. when a custom clip is removed)
        window.addEventListener(EDITOR_EVENTS.CLOSE_CLIP_EDITOR, () => {
            this.clipEditor?.close();
        });

        // Capture the import context before the importing tool changes selection.
        // AI callers await adoption; legacy producers get a caught error.
        window.addEventListener('trace-catalog:import', (event: Event) => {
            const detail = (event as CustomEvent<CatalogImportDetail>).detail;
            const sceneObject = detail?.sceneObject;
            if (!sceneObject || !this.viewer?.traceScene) return;
            acknowledgeCatalogImport(detail, this.adoptCatalogImport(detail));
        });

        // Listen for catalog scene opens (replacing current scene)
        window.addEventListener('trace-catalog:open', (event: Event) => {
            const detail = (event as CustomEvent<{ sceneId?: string; cms?: CmsManager; asset?: SourceAsset }>).detail;
            const cms = detail?.cms;
            const asset = detail?.asset;

            this.onSceneLoaded();

            // If a CMS scene was opened, wire up the publish flow. The catalog
            // already constructed and ran the CmsManager for us — adopt it
            // (after onSceneLoaded disconnected the previous CMS state).
            if (cms) {
                this.cmsManager = cms;
                setExportCmsManager(cms);
                setActiveCmsManager(cms);
                this._editorSession.open({ assetId: cms.sceneId }, null);
                // Publish gated on a usable Root state (see CMS bootstrap above).
                if (cms.rootLoaded || cms.rootMissing) {
                    this.showPublishButtons('cms-scene');
                    this.setupCmsStatusDot();
                } else {
                    console.warn('[Sandbox] CMS Scene loaded with broken RootContent — Publish disabled');
                }
            } else if (asset?.assetId) {
                // Asset-originated scene: enable republish via the same export
                // menu button. The snapshot comes from `assetContentSnapshot`
                // and so does the one `publishAsset` compares against — see
                // that module for why they may not be written out separately.
                //
                // `doOpenAsset` has already adopted the document root (D8), so
                // this baseline is taken on the adopted tree and a freshly
                // opened document is not dirty.
                const snapshot = assetContentSnapshot(this.viewer?.traceScene);
                this._editorSession.open(
                    { assetId: asset.assetId, title: asset.title, teamId: asset.teamId },
                    snapshot,
                );
                // Keep catalog-shared in sync for UI components that read it
                setSourceAsset(asset);
                // The asset's folder is now the editor's write target: an
                // import goes into it eagerly and a publish sends only what
                // it does not already hold (CONTENT_MODEL_SPEC §2.2, §2.6).
                this.openAssetFolder(asset);
                this.showPublishButtons('asset');
                this.setupCmsStatusDot();
            }
            // Re-select the catalog pane after the inspector remounts
            setTimeout(() => {
                const shell = getShellService();
                if (shell) {
                    const catalogPane = shell.sidePanes.find(p => p.key === 'catalog-panel');
                    catalogPane?.select();
                }
            }, 200);
        });

        // Listen for AI tool-driven scene changes (e.g. create_object, delete_object)
        window.addEventListener('trace-ai:scene-changed', () => {
            this.updateEmptySceneOverlay();
            // AI deletes bypass our selection-changed listener; drop any selected
            // objects that were removed so the outline/group-transform don't touch
            // disposed nodes.
            this.multiSelection?.pruneMissing();
        });

        // If the XR session ends externally (e.g. Quest back button) while in
        // play mode, exit play mode so the editor state is restored.
        window.addEventListener('trace-viewer:xr-state-changed', (event: Event) => {
            const detail = (event as CustomEvent<{ state?: string }>).detail;
            // Only react to the session ending (state = "available" after being active),
            // not intermediate states like "entering" or "exiting".
            if (detail?.state === 'available' && this.viewer?.isPlayMode()) {
                void this.togglePlayMode();
            }
        });

        // Keyboard shortcuts
        window.addEventListener('keydown', (e: KeyboardEvent) => {
            // Ctrl/Cmd+K: Toggle AI chat panel (works even from input fields)
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                this.chatPanelControls?.toggle();
                return;
            }

            // Don't handle other shortcuts if user is typing in an input field
            // (DOM inputs or Babylon GUI inputs rendered on canvas)
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable ||
                this.viewer?.traceScene?.hasGUIFocus) {
                return;
            }

            // Don't handle shortcuts with modifier keys (except specific combos below)
            const hasModifier = e.ctrlKey || e.metaKey || e.altKey;

            // Ctrl/Cmd+D: Duplicate selected object
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
                e.preventDefault();
                void this.duplicateSelectedObject();
                return;
            }

            // Delete/Backspace: Delete selected object
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                this.deleteSelectedObject();
                return;
            }

            // Alt+Shift+A: Toggle selected object active state
            if (e.altKey && e.shiftKey && e.code === 'KeyA') {
                e.preventDefault();
                this.toggleSelectedObjectActive();
                return;
            }

            // Alt+Shift+D: Toggle debug "select any node" mode
            if (e.altKey && e.shiftKey && e.code === 'KeyD') {
                e.preventDefault();
                settingsStore.debugSelectAnyNode = !settingsStore.debugSelectAnyNode;
                console.log(`[Sandbox] Debug select any node: ${settingsStore.debugSelectAnyNode ? 'enabled' : 'disabled'}`);
                return;
            }

            if (hasModifier) {
                return;
            }

            // F5: Toggle play mode
            if (e.key === 'F5') {
                e.preventDefault();
                void this.togglePlayMode();
                return;
            }

            // Escape closes any open toolbar menu
            if (e.key === 'Escape' && document.querySelector('.dropdown.open')) {
                e.preventDefault();
                this.closeDropdowns();
                return;
            }

            const addMenuOpen = this.newObjectDropdown.classList.contains('open');

            // Sub-hotkeys when add menu is open
            if (addMenuOpen) {
                // I: Import file
                if (e.key.toLowerCase() === 'i') {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.newObjectDropdown.classList.remove('open');
                    this.pickFiles('import');
                    return;
                }

                // O: Open file (replaces the current scene)
                if (e.key.toLowerCase() === 'o') {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.newObjectDropdown.classList.remove('open');
                    this.pickFiles('open');
                    return;
                }

                const keyToTemplate: Record<string, string> = {
                    's': 'empty',
                    'c': 'cube',
                    'h': 'sphere',
                    'e': 'plane',
                    'y': 'cylinder',
                    'n': 'astronaut',
                    't': 'text',
                    'g': 'pages',
                    'r': 'particle-emitter',
                    'l': 'timeline-widget',
                };

                const template = keyToTemplate[e.key.toLowerCase()];
                if (template) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.newObjectDropdown.classList.remove('open');
                    void this.createNewObject(template);
                    return;
                }
            }

            // Shift+A: Toggle add menu
            if (e.key.toLowerCase() === 'a' && e.shiftKey) {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.toggleDropdown(this.newObjectDropdown);
                return;
            }

            // B: Open add behavior menu (when add dropdown is closed)
            if (e.key.toLowerCase() === 'b') {
                const addBehaviorBtn = document.querySelector<HTMLButtonElement>(
                    'button[data-action="add-behavior"]'
                );
                if (addBehaviorBtn) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    addBehaviorBtn.click();
                }
                return;
            }

            // S: Set keyframe (when clip editor is active)
            if (e.key.toLowerCase() === 's') {
                if (this.clipEditor?.isActive()) {
                    e.preventDefault();
                    this.clipEditor.setKey();
                    return;
                }
            }

            // C: Toggle clip editor (when selected object has Animation behavior)
            if (e.key.toLowerCase() === 'c' && !e.shiftKey) {
                if (this.clipEditor?.isActive()) {
                    e.preventDefault();
                    this.clipEditor.close();
                    return;
                }
                const anim = this.selectedObject?.getBehavior(Animation);
                if (anim) {
                    e.preventDefault();
                    const hasClipSelected = anim.getData().clip;
                    window.dispatchEvent(new CustomEvent(
                        hasClipSelected ? EDITOR_EVENTS.EDIT_CLIP : EDITOR_EVENTS.CREATE_CLIP,
                    ));
                    return;
                }
            }

            // Shift+C: Create new clip (when selected object has Animation behavior)
            if (e.key.toLowerCase() === 'c' && e.shiftKey) {
                if (this.selectedObject?.getBehavior('Animation')) {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.CREATE_CLIP));
                    return;
                }
            }

            // F: Focus camera on selected object (or frame entire scene)
            if (e.key.toLowerCase() === 'f' && !e.shiftKey) {
                e.preventDefault();
                this.focusCameraOnSelection();
                return;
            }
        });

        // Shift modifier: fast camera panning & zoom
        this.setupShiftCameraModifier();

        // WASD camera movement (while mouse is held down)
        this.setupWASDCameraMovement();
    }

    /**
     * Focus the camera on the selected object, or frame the entire scene
     * if nothing is selected.
     *
     * First tap: tight framing on the object.
     * Second tap (within 500ms): wider framing (2x radius scale).
     * Toggles between tight/wide on repeated taps.
     */
    private focusCameraOnSelection(): void {
        if (!this.viewer) return;

        // Double-tap detection: toggle tight ↔ wide
        const now = performance.now();
        const isDoubleTap = (now - this.lastFocusTime) < 500;
        this.lastFocusTime = now;

        if (isDoubleTap) {
            this.focusIsWide = !this.focusIsWide;
        } else {
            this.focusIsWide = false;
        }

        const radiusScale = this.focusIsWide ? 3 : 1.5;
        const selected = this.multiSelection?.getSelectedObjects() ?? [];

        if (selected.length > 1) {
            // Multi-selection — frame the combined bounds of all objects.
            this.viewer.focusObjects(selected, { radiusScale });
        } else if (this.selectedObject) {
            this.viewer.focusObject(this.selectedObject, { radiusScale });
        } else {
            // No selection — frame entire scene (same as double-click)
            this.viewer.resetCamera(true);
        }
    }

    /**
     * Sets up Shift key modifier for fast camera panning and zooming.
     * While Shift is held: panningSensibility drops (faster panning),
     * wheelDeltaPercentage increases (faster zoom).
     */
    private setupShiftCameraModifier(): void {
        // Default editor camera values (from inspector-extension.tsx)
        const NORMAL_PANNING = 200;
        const FAST_PANNING = 50;
        const NORMAL_WHEEL = 0.025;
        const FAST_WHEEL = 0.075;

        let shiftActive = false;

        const getCamera = (): ArcRotateCamera | null => {
            const cam = this.viewer?.scene?.activeCamera;
            return cam instanceof ArcRotateCamera ? cam : null;
        };

        const setFast = (fast: boolean) => {
            const camera = getCamera();
            if (!camera) return;
            shiftActive = fast;
            camera.panningSensibility = fast ? FAST_PANNING : NORMAL_PANNING;
            camera.wheelDeltaPercentage = fast ? FAST_WHEEL : NORMAL_WHEEL;
        };

        window.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Shift' && !shiftActive) {
                setFast(true);
            }
        });

        window.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.key === 'Shift' && shiftActive) {
                setFast(false);
            }
        });

        window.addEventListener('blur', () => {
            if (shiftActive) {
                setFast(false);
            }
        });
    }

    /**
     * WASD camera movement while any mouse button is held down on the canvas.
     * Moves the camera target in the camera's local XZ plane (relative to view direction).
     * Movement runs on the render loop for smooth per-frame animation.
     */
    private setupWASDCameraMovement(): void {
        const keysDown = new Set<string>();
        let mouseDown = false;

        const getCamera = (): ArcRotateCamera | null => {
            const cam = this.viewer?.scene?.activeCamera;
            return cam instanceof ArcRotateCamera ? cam : null;
        };

        const getCanvas = () => this.viewer?.scene?.getEngine().getRenderingCanvas();

        // Track mouse state on the canvas
        const onPointerDown = () => { mouseDown = true; };
        const onPointerUp = () => { mouseDown = false; };

        // Attach/re-attach pointer listeners when the canvas element changes
        let currentCanvas: HTMLCanvasElement | null = null;
        const ensureCanvasListeners = () => {
            const c = getCanvas();
            if (c === currentCanvas) return;
            if (currentCanvas) {
                currentCanvas.removeEventListener('pointerdown', onPointerDown);
                currentCanvas.removeEventListener('pointerup', onPointerUp);
            }
            currentCanvas = c ?? null;
            if (currentCanvas) {
                currentCanvas.addEventListener('pointerdown', onPointerDown);
                currentCanvas.addEventListener('pointerup', onPointerUp);
            }
        };

        // Track WASD keys globally so they work even when canvas loses focus mid-drag
        window.addEventListener('keydown', (e: KeyboardEvent) => {
            // Ignore when modifiers are held (e.g. Cmd+D to duplicate)
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const key = e.key.toLowerCase();
            if (key === 'w' || key === 'a' || key === 's' || key === 'd' || key === 'q' || key === 'e') {
                if (mouseDown) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
                keysDown.add(key);
            }
        });

        window.addEventListener('keyup', (e: KeyboardEvent) => {
            // macOS swallows individual keyup events while Meta is held,
            // so clear everything when Meta itself is released
            if (e.key === 'Meta') {
                keysDown.clear();
            } else {
                keysDown.delete(e.key.toLowerCase());
            }
        });

        window.addEventListener('blur', () => {
            keysDown.clear();
            mouseDown = false;
        });

        // Lazily attach a before-render observer, re-attaching when the scene changes
        let attachedScene: unknown = null;

        const tick = () => {
            ensureCanvasListeners();

            // Re-attach observer if scene changed (e.g. after open/clear)
            const scene = this.viewer?.scene;
            if (scene && scene !== attachedScene) {
                attachedScene = scene;
                scene.onBeforeRenderObservable.add(tick);
                return; // will run on next frame via the new observer
            }

            if (!mouseDown || keysDown.size === 0) return;

            const camera = getCamera();
            if (!camera || !this.viewer?.scene) return;

            const engine = this.viewer.scene.getEngine();
            const deltaMs = engine.getDeltaTime();
            const baseSpeed = 2.0; // units per second
            const speed = (baseSpeed * deltaMs) / 1000;

            // Scale by camera radius so speed feels consistent at any zoom level
            const moveAmount = speed * camera.radius * 0.5;

            // Camera-relative directions projected onto the XZ ground plane
            const forward = camera.target.subtract(camera.position);
            forward.y = 0;
            forward.normalize();

            const right = Vector3.Cross(forward, Vector3.Up()).normalize();

            const delta = Vector3.Zero();
            if (keysDown.has('w')) delta.addInPlace(forward);
            if (keysDown.has('s')) delta.subtractInPlace(forward);
            if (keysDown.has('d')) delta.addInPlace(right);
            if (keysDown.has('a')) delta.subtractInPlace(right);
            if (keysDown.has('e')) delta.addInPlace(Vector3.Up());
            if (keysDown.has('q')) delta.subtractInPlace(Vector3.Up());

            if (delta.lengthSquared() === 0) return;
            delta.normalize().scaleInPlace(moveAmount);

            camera.target.addInPlace(delta);
        };

        // Kick off the lazy-attach loop via requestAnimationFrame until scene exists
        const waitForScene = () => {
            const scene = this.viewer?.scene;
            if (scene) {
                attachedScene = scene;
                scene.onBeforeRenderObservable.add(tick);
            } else {
                requestAnimationFrame(waitForScene);
            }
        };
        waitForScene();
    }

    private async createEmptyViewer(): Promise<void> {
        console.log('[Sandbox] Creating empty scene...');

        // Create new viewer without a src - this will create an empty scene
        this.viewer = document.createElement('trace-viewer');

        // Apply stored settings from inspector extension
        this.viewer.setAttribute('backgroundcolor', settingsStore.backgroundColor);
        this.viewer.setAttribute('cameraradius', settingsStore.cameraRadius.toString());
        this.viewer.setAttribute('alpha', settingsStore.alpha.toString());
        this.viewer.setAttribute('cameraBeta', settingsStore.cameraBeta.toString());
        this.viewer.setAttribute('prewarmParticles', settingsStore.prewarmParticles.toString());
        this.viewer.setAttribute('logger', settingsStore.logger);
        // This is an editor: start in edit mode, so a scene opened at boot is
        // an authoring load (a published scene re-opens its originals, not
        // the baked copies). The play button toggles from here.
        this.viewer.setAttribute('play-mode', 'false');

        this.viewerWrapper.appendChild(this.viewer);

        // Wait for the viewer to initialize
        await new Promise<void>((resolve) => {
            const checkScene = setInterval(() => {
                if (this.viewer?.scene) {
                    clearInterval(checkScene);
                    console.log('[Sandbox] Empty scene created');

                    // Initialize undo manager with the scene
                    getUndoManager().traceScene = this.viewer.traceScene ?? null;

                    // Reference-identity registry: interaction-target paths
                    // are derived from SceneObjectRefs on every topology
                    // change (reparent — Inspector drags included — rename,
                    // delete, create), so no mutation surface needs its own
                    // repoint bracket. Supersedes the reparent-observer
                    // install. Lives as long as the TraceScene; heals are
                    // not undo entries (undoing the structural change
                    // re-derives the old strings by itself).
                    if (this.viewer.traceScene) {
                        installReferenceIdentity(this.viewer.traceScene);
                        // Auto-separate on group entry: a live reparent that
                        // drops a stateful object with a FOREIGN axis (no
                        // shared state names) into a group marks it
                        // stateGroup:true, so independence is explicit data
                        // (Unity/TF read it) instead of a walk-time heuristic.
                        // Loads never fire it; undo/redo and play mode are
                        // gated inside. Lives as long as the TraceScene.
                        installAutoSeparateOnGroupEntry(this.viewer.traceScene);
                        // Triplanar preview for the editing session: the engine
                        // re-projects smart components itself on every load and
                        // edit from here on (tv#704). Idempotent per TraceScene.
                        enableTriplanarPreview(this.viewer.traceScene);
                    }

                    // Editor multi-object selection. Routes selection-changed
                    // events (incl. the ctrl/shift additive hint) into the
                    // SelectionModel and drives the multi-highlight outline.
                    this.multiSelection?.dispose();
                    this.multiSelection = new MultiSelectionService(this.viewer);
                    this.multiSelection.onPrimaryChanged = (primary) => this.applySelectedObject(primary);
                    // Publish the set for actions that are handed one object
                    // and need the whole selection — "Save as asset" on a
                    // multi-select (spec §3.5). A getter, so the reader always
                    // sees the live model rather than a snapshot of it.
                    setActiveSelectionSource(() => this.multiSelection?.getSelectedObjects() ?? []);

                    // Group transform (centroid pivot) — drives multi-object
                    // move/rotate/scale off the same selection model.
                    this.groupTransform?.dispose();
                    this.groupTransform = new GroupTransformService(this.viewer, this.multiSelection.getModel());

                    // Provide TraceScene accessor for gizmo keyboard GUI focus checks
                    setGizmoKeyboardTraceScene(() => this.viewer?.traceScene ?? null);

                    // Enable edit mode (play mode = false) so clicks select objects instead of executing interactions
                    this.viewer.setPlayMode(false);
                    abandonPlayMode();
                    this.updatePlayModeButton(false);

                    // Enable click-to-select for the behaviors panel
                    this.viewer.enableClickToSelect(true);
                    setupViewportContextMenu();
                    setupViewportTextEditor();

                    // Enable grid by default
                    this.createGrid();
                    this.updateGridButton();

                    // Apply stored settings (camera beta, etc.)
                    applyStoredSettings();

                    // Auto-open the inspector
                    this.openInspector();

                    // Mount AI chat panel
                    this.chatPanelControls = mountChatPanel(
                        this.viewerWrapper,
                        () => this.viewer?.traceScene ?? null,
                        () => this.selectedObject,
                        () => this.viewer ?? null,
                        files => this.importFiles(files),
                    );

                    // Mount XR voice assistant (activates when entering immersive XR)
                    this.xrAssistantControls = mountXRAssistant(
                        () => this.viewer?.traceScene ?? null,
                        () => this.viewer?.traceScene?.getBabylonScene() ?? null,
                    );

                    // Mount clip editor overlay
                    this.clipEditor = mountClipEditor();

                    // Mount page context overlay
                    this.pageContext = mountPageContext();

                    // Mount keyframe context overlay
                    this.keyframeContext = mountKeyframeContext();

                    // Mount state-group context overlay (Properties analog of the
                    // keyframe strip).
                    this.stateContext = stripForScene(this.stateContext, this.viewer.traceScene ?? null);

                    // Route Properties commits into the selected keyframe.
                    // Idempotent across scene reloads: dispose any prior listener.
                    this.keyframePropertyCaptureDispose?.();
                    this.keyframePropertyCaptureDispose = initKeyframePropertyCapture(
                        () => this.viewer?.traceScene ?? null,
                    );

                    // State-group coordination (shared trace-viewer/editor core).
                    // No mutation guard: with none configured, states actions
                    // refuse edits while the scene is in Play.
                    configureStateActions({});
                    // Warn (dev console) on a setState against an undeclared state —
                    // surfaces a reference the rename/delete heal missed. DEV-only
                    // to match the codebase's dev-diagnostics convention.
                    setStateDiagnostics(import.meta.env.DEV);

                    // Edit-mode AutoUI variant-swatch picks route through the editor
                    // (into the active state / declaration base, with undo + preview
                    // regen) — parity with the behaviors panel. Play-mode picks stay
                    // runtime-tier (handled inside AutoUI). Installed once (idempotent).
                    installAutoUISwatchEditorCommit();

                    // Show empty scene overlay
                    this.updateEmptySceneOverlay();

                    // Audio gizmo: billboard markers so AudioClip objects
                    // (which have no inherent visual) are selectable. Fire
                    // and forget — controller is harmless to construct
                    // asynchronously, and rescans itself on scene-load events.
                    void createAudioGizmoController(this.viewer).then((ctrl) => {
                        if (!ctrl) return;
                        // Sandbox may have torn the viewer down (e.g. rapid
                        // scene swaps) before the dynamic import resolved.
                        if (!this.viewer) { ctrl.dispose(); return; }
                        this.audioGizmoController = ctrl;
                        // Match current play-mode state at creation time.
                        ctrl.setVisible(!(this.viewer.isPlayMode?.() ?? false));
                    });

                    resolve();
                }
            }, 100);
        });

        // A new scene starts with a document root (D8), so the first import
        // has somewhere to land and the first save writes a D8 document. A
        // scene-establishing load clears it again and re-adopts on its own.
        await adoptDocumentRoot(this.viewer.traceScene, null);
        this.updateEmptySceneOverlay();
    }

    /** Build ShowInspector options, adapting layout for mobile viewports. */
    private getInspectorOptions(): Partial<InspectorOptions> {
        const mobile = isMobile();
        console.log('[Sandbox] Inspector options — mobile:', mobile);
        return {
            serviceDefinitions: [
                SelectionBridgeServiceDefinition,
                BehaviorsPanelServiceDefinition,
                DesignPanelServiceDefinition,
                TraceViewerServiceDefinition,
                TraceExportServiceDefinition,
                GizmoKeyboardServiceDefinition,
                PlanarGizmoServiceDefinition,
                UndoGizmoServiceDefinition,
                GizmoSnapServiceDefinition,
                BoundingBoxGizmoServiceDefinition,
                LayoutGizmoServiceDefinition,
                PlanarDragServiceDefinition,
                PlanarDragToolbarDefinition,
                ExportContextMenuServiceDefinition,
                InferenceContextMenuServiceDefinition,
                CatalogPanelServiceDefinition,
                ResourcesPanelServiceDefinition,
            ],
            // Hide panes that don't apply; on mobile collapse to a single right column
            sidePaneRemapper: mobile
                ? (pane: { key: string }) => {
                    const mobilePanes = new Set(['Scene Explorer', 'behaviors-panel', 'trace-viewer-settings']);
                    if (!mobilePanes.has(pane.key)) return null;
                    return { horizontalLocation: 'right' as const, verticalLocation: 'top' as const };
                }
                : (pane: { key: string; horizontalLocation: string; verticalLocation: string }) => {
                    const hiddenPanes = new Set(['Tools']);
                    if (hiddenPanes.has(pane.key)) return null;
                    return { horizontalLocation: pane.horizontalLocation, verticalLocation: pane.verticalLocation } as { horizontalLocation: 'left' | 'right'; verticalLocation: 'top' | 'bottom' };
                },
            // On mobile, overlay the sidebar so the canvas keeps its full width
            ...(mobile && {
                layoutMode: 'overlay' as const,
                showThemeSelector: false,
                // Start with pane content collapsed
                rightPaneDefaultCollapsed: true,
                leftPaneDefaultCollapsed: true,
            }),
        };
    }

    private openInspector(): void {
        if (!this.viewer?.scene) {
            return;
        }

        const scene = this.viewer.scene;

        try {
            console.log('[Sandbox] Opening Inspector...');

            // Before the Inspector builds its services: the scene tree starts
            // at the DOCUMENT ROOT, never at the engine mount point (D8).
            installDocumentRootSceneTree(() => this.viewer?.traceScene ?? null);

            this.inspectorDisposable = ShowInspector(scene, this.getInspectorOptions());

            this.inspectorVisible = true;
            console.log('[Sandbox] Inspector opened');

            // Safe-area tracking waits for the panes itself, so it starts now
            // rather than on a timeout that a slow first paint can outrun.
            this.setupSafeAreaTracking();

            // Post-mount actions after inspector panels have rendered
            setTimeout(() => {
                reportSceneTreeReroot();
                this.expandDocumentRootAndSelect();
                this.setupPageContextBridge();
            }, 150);
        } catch (error) {
            console.error('[Sandbox] Failed to open inspector:', error);
        }
    }

    /**
     * Track the inspector's pane widths and mirror them onto the viewer's safe
     * area insets, so in-scene GUI overlays (AutoUI) render in the slice of the
     * canvas the panes leave visible instead of underneath them.
     */
    private setupSafeAreaTracking(): void {
        this._disposeSafeAreaTracking?.();
        this._disposeSafeAreaTracking = observeInspectorPaneInsets(({ left, right }) => {
            this.viewer?.setSafeAreaInsets({ left, right, top: 0, bottom: 0 });
        });
    }

    /**
     * Listen to the Inspector's selection service so that hierarchy-driven
     * selection changes also update the page context overlay.
     * (The `trace-viewer:selection-changed` event only fires for viewport clicks
     *  and programmatic select() calls — hierarchy clicks go through the bridge
     *  which may complete after our window listener runs.)
     */
    private setupPageContextBridge(): void {
        const selectionService = getSelectionService();
        if (!selectionService || !this.pageContext) return;

        selectionService.onSelectedEntityChanged.add(() => {
            // Use a microtask so the selection bridge has time to call traceScene.select()
            // and our window handler has updated this.selectedObject.
            void Promise.resolve().then(() => {
                this.pageContext?.update(this.selectedObject);
                this.keyframeContext?.update(this.selectedObject);
                this.stateContext?.update(this.selectedObject);
            });
        });
    }

    /**
     * Expand the Nodes section and the document root in the Inspector tree by
     * briefly selecting a node (which causes the tree to expand its ancestors),
     * then immediately clearing the selection so nothing appears selected.
     */
    private expandDocumentRootAndSelect(): void {
        const selectionService = getSelectionService();
        if (!selectionService) return;

        // The DOCUMENT ROOT, or its first child — never the engine mount
        // point, which the tree does not show and nothing may select (D8).
        const root = documentRoot(this.viewer?.traceScene);
        if (!root) return;
        const children = root.transform.getChildren();
        const target = children.length > 0 ? children[0] : root.transform;

        // Suppress the bridge so this doesn't affect the viewport (no gizmos, no events)
        suppressSelectionBridge(true);
        selectionService.selectedEntity = target;

        // Clear selection on the next frame so the tree stays expanded
        // but nothing remains visually selected.
        requestAnimationFrame(() => {
            selectionService.selectedEntity = null;
            suppressSelectionBridge(false);
        });
    }

    private async loadScene(): Promise<void> {
        const value = this.dataFilesSelector.value;

        // Ensure we have a viewer
        if (!this.viewer) {
            await this.createEmptyViewer();
        }

        // Destroy grid before loading (it will be recreated in onSceneLoaded if enabled)
        this.destroyGrid();

        // If no value, clear to empty scene
        if (!value) {
            console.log('[Sandbox] Clearing to empty scene...');
            this.viewer!.clearScene();
            // `clearScene()` deliberately creates no root — when a session
            // starts is the editor's decision (D8), and this is that decision.
            await adoptDocumentRoot(this.viewer!.traceScene, null);
            // Recreate grid for empty scene if enabled
            if (this.gridEnabled) {
                this.createGrid();
            }
            // Apply stored settings to the fresh scene
            applyStoredSettings();
            // Inspector stays alive — re-expand the document root in the explorer tree
            if (this.inspectorVisible) {
                setTimeout(() => this.expandDocumentRootAndSelect(), 150);
            }
            this.updateEmptySceneOverlay();
            return;
        }

        // Parse the JSON value to get src and optional behaviors
        const entry = JSON.parse(value) as { src: string; behaviors?: BehaviorData[] };

        console.log(`[Sandbox] Loading scene: ${entry.src}${entry.behaviors?.length ? ` with ${entry.behaviors.length} behaviors` : ''}`);

        // Ensure path is absolute to avoid relative path resolution issues
        const absoluteSrc = entry.src.startsWith('/') ? entry.src : `/${entry.src}`;

        // Use the open() API to load the new scene with behaviors
        await this.viewer!.open({ src: absoluteSrc, behaviors: entry.behaviors });
        await adoptDocumentRoot(this.viewer!.traceScene, null);

        this.onSceneLoaded();
    }

    private onSceneLoaded(): void {
        console.log('[Sandbox] Scene loaded successfully');

        if (!this.viewer?.scene) {
            console.error('[Sandbox] Scene not available');
            return;
        }

        // Notify Resources panel that scene resources have been (re)loaded
        dispatchResourcesChanged({ action: 'loaded', path: null });

        // Update undo manager with new scene reference and clear history
        const undoManager = getUndoManager();
        undoManager.traceScene = this.viewer.traceScene ?? null;
        undoManager.clear();

        // Bridge runtime-tier setValue writes (AutoUI swatch, graph taps) to
        // BEHAVIOR_CHANGED so state/keyframe thumbnails recapture — closing the
        // asymmetry with behaviors-panel commits.
        if (this.viewer.traceScene) {
            installSetValueThumbnailBridge(this.viewer.traceScene);
        }

        // Disconnect CMS publish tracking — we may no longer be editing a CMS scene.
        // Call sites that open CMS scenes re-establish this after onSceneLoaded().
        this.disconnectCmsPublish();

        // Enable edit mode (play mode = false) so clicks select objects instead of executing interactions
        this.viewer.setPlayMode(false);
        abandonPlayMode();
        this.updatePlayModeButton(false);

        // A scene open may hand us a fresh TraceScene; arming is idempotent.
        if (this.viewer.traceScene) enableTriplanarPreview(this.viewer.traceScene);
        this.stateContext = stripForScene(this.stateContext, this.viewer.traceScene ?? null);

        // Enable click-to-select for the behaviors panel
        this.viewer.enableClickToSelect(true);
        setupViewportContextMenu();
        setupViewportTextEditor();

        // Enable grid by default if it was enabled before
        if (this.gridEnabled) {
            this.createGrid();
            this.updateGridButton();
        }

        // Apply stored inspector settings (camera angle, background, etc.)
        applyStoredSettings();

        // Inspector stays alive across scene transitions (scene object is reused).
        // Re-expand the document root in the explorer tree so the new content is visible.
        if (this.inspectorVisible) {
            setTimeout(() => this.expandDocumentRootAndSelect(), 150);
        }

        this.updateEmptySceneOverlay();
    }


    private async togglePlayMode(): Promise<void> {
        if (!this.viewer) {
            return;
        }

        const traceScene = this.viewer.traceScene;
        const newPlayMode = !this.viewer.isPlayMode();
        if (!traceScene) return;

        if (newPlayMode) enterPlayMode(traceScene);
        this.updatePlayModeButton(newPlayMode);
        // Hide editor-only chrome in play mode so previews look clean.
        this.audioGizmoController?.setVisible(!newPlayMode);

        if (newPlayMode) {
            // Entering play mode — hide grid if visible, enter XR if available
            this._gridWasEnabled = this.gridEnabled;
            if (this.gridEnabled) {
                this.setGridVisible(false);
            }
            await this._enterPlayModeXR();
        } else {
            // Exiting play mode — exit XR, restore scene state, then restore grid
            await this._exitPlayModeXR();
            try {
                await exitPlayMode(traceScene);
            } catch (err) {
                console.error('[Sandbox] Error restoring play mode snapshot:', err);
            } finally {
                this.updateEmptySceneOverlay();
            }
            // Re-show the grid (not recreate) so it keeps its original sizing
            if (this._gridWasEnabled) {
                this.setGridVisible(true);
            }
        }

        console.log(newPlayMode ? '[Sandbox] Play mode enabled' : '[Sandbox] Edit mode enabled');
    }

    /**
     * When entering play mode on an XR-capable device, enter immersive AR
     * and disable the utility layer (grid/gizmos) which renders incorrectly
     * in stereo XR.
     */
    private async _enterPlayModeXR(): Promise<void> {
        if (!this.viewer?.isXRSupported) return;

        // Deselect to remove gizmos
        const selectionService = getSelectionService();
        if (selectionService) {
            selectionService.selectedEntity = null;
        }

        try {
            await this.viewer.enterXR();
        } catch (err) {
            console.warn('[Sandbox] Failed to enter XR for play mode:', err);
        }
    }

    /**
     * When exiting play mode, exit XR and restore the utility layer.
     */
    private async _exitPlayModeXR(): Promise<void> {
        // Exit XR if active
        if ((this.viewer?.xrState as string) === 'active') {
            try {
                await this.viewer!.exitXR();
            } catch (err) {
                console.warn('[Sandbox] Failed to exit XR:', err);
            }
        }
    }

    private toggleGrid(): void {
        if (!this.viewer?.scene) {
            return;
        }

        const scene = this.viewer.scene;

        // Toggle grid visibility; create on first use, then just show/hide
        if (this.gridEnabled) {
            this.setGridVisible(false);
        } else if (gridStore(scene).gridMesh) {
            this.setGridVisible(true);
        } else {
            this.createGrid();
            this.gridEnabled = true;
            this.updateGridButton();
        }
    }

    private createGrid(): void {
        if (!this.viewer?.scene) {
            return;
        }

        const renderScene = this.viewer.scene;
        const store = gridStore(renderScene);

        // Destroy existing grid first to prevent doubles
        if (store.gridMesh) {
            this.destroyGrid();
        }

        // Use the utility layer scene like Inspector does
        const utilityScene = UtilityLayerRenderer.DefaultKeepDepthUtilityLayer.utilityLayerScene;

        // Fixed 100m square grid. Sized in world meters via builder dimensions (scaling stays 1)
        // so GridMaterial.gridRatio is directly world meters per minor cell — no per-scene math.
        const gridMesh = CreateGround("grid", { width: GRID_SIZE_M, height: GRID_SIZE_M, subdivisions: 1 }, utilityScene);
        gridStore(gridMesh).isInspectorGrid = true;
        gridMesh.isPickable = false;

        // Create grid material — read current values from inspector settings store so
        // toggling the grid off/on preserves the user's inspector adjustments.
        const groundMaterial = new GridMaterial("GridMaterial", utilityScene);
        groundMaterial.majorUnitFrequency = settingsStore.gridMajorUnitFrequency;
        groundMaterial.minorUnitVisibility = settingsStore.gridMinorUnitVisibility;
        groundMaterial.gridRatio = settingsStore.gridRatio;
        groundMaterial.backFaceCulling = false;
        groundMaterial.mainColor = new Color3(1, 1, 1);
        groundMaterial.lineColor = new Color3(1.0, 1.0, 1.0);
        groundMaterial.opacity = settingsStore.gridOpacity;
        // linesOnly replaces Babylon 9.7's removal of the implicit opacity-driven transparency between grid lines
        groundMaterial.linesOnly = true;
        groundMaterial.zOffset = 1.0;

        // Load opacity texture
        const textureUrl = Tools.GetAssetUrl("https://assets.babylonjs.com/core/environments/backgroundGround.png");
        groundMaterial.opacityTexture = new Texture(textureUrl, utilityScene);

        gridMesh.material = groundMaterial;

        // Store in scene's reservedDataStore (shared with Inspector)
        store.gridMesh = gridMesh;

        console.log('[Sandbox] Grid enabled');
    }

    private destroyGrid(): void {
        if (!this.viewer?.scene) {
            return;
        }

        const scene = this.viewer.scene;
        const store = gridStore(scene);
        const gridMesh = store.gridMesh;

        if (gridMesh) {
            gridMesh.dispose(true, true);
            store.gridMesh = null;
        }

        console.log('[Sandbox] Grid disabled');
    }

    /**
     * Show or hide the grid mesh without destroying/recreating it.
     * Avoids recalculating extents which can produce wrong sizing after play mode.
     */
    private setGridVisible(visible: boolean): void {
        const gridMesh = this.viewer?.scene ? gridStore(this.viewer.scene).gridMesh : null;
        if (gridMesh) {
            gridMesh.setEnabled(visible);
        }
        this.gridEnabled = visible;
        this.updateGridButton();
    }

    private updateGridButton(): void {
        if (this.gridEnabled) {
            this.toggleGridBtn.classList.add('active');
        } else {
            this.toggleGridBtn.classList.remove('active');
        }
    }

    /** Show or hide the empty-scene overlay based on whether the scene has any user objects. */
    private updateEmptySceneOverlay(): void {
        // Content is what the DOCUMENT ROOT holds: every session has one, so
        // counting the engine root's children would say "not empty" forever.
        const hasObjects = topLevelObjects(this.viewer?.traceScene).length > 0;
        const isPlayMode = this.viewer?.isPlayMode() ?? false;
        this.emptySceneOverlay.classList.toggle('visible', !hasObjects && !isPlayMode);
    }


    private updatePlayModeButton(isPlayMode: boolean): void {
        if (isPlayMode) {
            this.togglePlayModeBtn.textContent = '⏹';
            this.togglePlayModeBtn.classList.add('active');
            this.togglePlayModeBtn.title = 'Stop (F5)';
        } else {
            this.togglePlayModeBtn.textContent = '▶';
            this.togglePlayModeBtn.classList.remove('active');
            this.togglePlayModeBtn.title = 'Play (F5)';
        }
    }

    /** Open on the toggle button, close on any click outside the dropdown. */
    private setupDropdown(dropdown: HTMLElement): void {
        const toggle = dropdown.querySelector('.dropdown-toggle') as HTMLButtonElement;
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleDropdown(dropdown);
        });
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target as Node)) {
                dropdown.classList.remove('open');
            }
        });
    }

    /** Toggle one toolbar menu; only one is open at a time. */
    private toggleDropdown(dropdown: HTMLElement): void {
        const open = !dropdown.classList.contains('open');
        this.closeDropdowns();
        dropdown.classList.toggle('open', open);
    }

    private closeDropdowns(): void {
        document.querySelectorAll('.dropdown.open').forEach((d) => d.classList.remove('open'));
    }

    private setupNewObjectDropdown(): void {
        this.setupDropdown(this.newObjectDropdown);
        const menu = this.newObjectDropdown.querySelector('.dropdown-menu') as HTMLDivElement;

        // Handle menu item clicks
        menu.addEventListener('click', (e) => {
            const button = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-template]');
            if (button?.dataset.template) {
                void this.createNewObject(button.dataset.template).then(() => {
                    this.newObjectDropdown.classList.remove('open');
                });
            }
        });

        // Open file button (replaces the current scene)
        document.getElementById('open-file-btn')!.addEventListener('click', () => {
            this.newObjectDropdown.classList.remove('open');
            this.pickFiles('open');
        });

        // Import file button
        document.getElementById('import-file-btn')!.addEventListener('click', () => {
            this.newObjectDropdown.classList.remove('open');
            this.pickFiles('import');
        });
    }

    private setupViewModeDropdown(): void {
        const dropdown = document.getElementById('view-mode-dropdown') as HTMLDivElement;
        this.setupDropdown(dropdown);
        const toggle = dropdown.querySelector('.dropdown-toggle') as HTMLButtonElement;
        const items = dropdown.querySelectorAll<HTMLButtonElement>('button[data-view-mode]');
        const update = () => {
            toggle.classList.toggle('active', settingsStore.viewMode !== 'shaded');
            items.forEach((item) => item.classList.toggle('selected', item.dataset.viewMode === settingsStore.viewMode));
        };
        items.forEach((item) => item.addEventListener('click', () => {
            dropdown.classList.remove('open');
            settingsStore.viewMode = item.dataset.viewMode as ViewMode;
            if (this.viewer?.scene) {
                setViewMode(this.viewer.scene, settingsStore.viewMode);
            }
            update();
        }));
        update();
    }

    private setupExportDropdown(): void {
        this.setupDropdown(this.exportDropdown);

        // Handle menu item clicks
        document.getElementById('export-download-glz')!.addEventListener('click', () => {
            this.exportDropdown.classList.remove('open');
            void this.exportSceneAsGlz();
        });
        document.getElementById('export-download-gltf')!.addEventListener('click', () => {
            this.exportDropdown.classList.remove('open');
            void this.exportSceneAsGltf();
        });
        const includeEditor = document.getElementById('export-include-editor') as HTMLInputElement;
        includeEditor.addEventListener('change', () => setIncludeEditorContentInDownloads(includeEditor.checked));
        document.getElementById('export-publish-gltf')!.addEventListener('click', () => {
            this.exportDropdown.classList.remove('open');
            void this.publishScene({ rootFormat: 'gltf' });
        });
        document.getElementById('export-publish-zip')!.addEventListener('click', () => {
            this.exportDropdown.classList.remove('open');
            void this.publishScene({ rootFormat: 'zip' });
        });
    }

    /**
     * If the current selection is inside a Keyframes container, return that
     * container, so new objects are created under it (and thus become part of
     * the keyframed subtree) instead of at the scene root. Mirrors the Pages
     * behaviour, but parents to the container itself: keyframes are named states
     * of one shared subtree, not per-keyframe child containers, so there is no
     * "active page child" to nest under. Uses the overlay's resolution so this
     * agrees with where keyframe edits route.
     */
    private getActiveKeyframeContainerForSelection(): SceneObject | null {
        if (!this.selectedObject) return null;
        return resolveKeyframeContext(this.selectedObject)?.container ?? null;
    }

    /**
     * If the current selection is inside a Pages container, return the active page
     * (the direct child of the container that contains the selection).
     * Returns null if not inside a pages context.
     */
    private getActivePageForSelection(): SceneObject | null {
        if (!this.selectedObject || !this.viewer?.traceScene) return null;
        // The SHARED context wins (getSharedContextPagesContainer) — callers
        // check it first, so a shared-context selection never reaches here.
        if (this.getSharedContextPagesContainer()) return null;

        // Delegate to the strip's resolution so creation parents exactly where
        // the strip says the context is. In particular this filters SHARED
        // children out of the page set and uses the behavior's active index —
        // a raw children.find(c => c.active) can return the Shared group
        // (always active) or a stale-active page node.
        const ctx = resolvePageContext(this.selectedObject);
        return ctx ? (ctx.selectedPage ?? ctx.activePage) : null;
    }

    /**
     * The Pages container whose SHARED GROUP should capture new/imported
     * content, or null. Two triggers:
     *  - the strip's "Shared" card is the active context for the container
     *    the selection sits in (`isSharedIntent`), or
     *  - the selection is inside a SHARED subtree (adding next to shared
     *    content keeps it shared — and prevents new objects from nesting
     *    UNDER a selected shared object, which is not a page).
     */
    private getSharedContextPagesContainer(): SceneObject | null {
        const selected = this.selectedObject;
        if (!selected || !this.viewer?.traceScene) return null;

        const selfPages = selected.getBehavior(Pages);
        if (selfPages) {
            if (isSharedIntent(selected)) return selected;
            // A container with NO pages yet (Pages behavior just added) has
            // only the Shared context — the strip shows the Shared card
            // active, so creation must match even before the strip's poll
            // syncs the intent flag.
            const scene = this.viewer.traceScene;
            const sharedObjs = new Set(
                (selfPages.getData().shared ?? [])
                    .map(p => scene.resolveSceneObject(p, selected))
                    .filter((o): o is SceneObject => !!o),
            );
            const hasPages = selected.getChildren().some(c => !sharedObjs.has(c));
            return hasPages ? null : selected;
        }

        let child = selected;
        let parent = child.getParent();
        while (parent) {
            const pagesBehavior = parent.getBehavior(Pages);
            if (pagesBehavior) {
                if (isSharedIntent(parent)) return parent;
                // Membership by RESOLUTION (runtime fail-safe): is the direct
                // child the selection sits under a registered shared child?
                const shared = pagesBehavior.getData().shared ?? [];
                const scene = this.viewer.traceScene;
                const target = parent;
                const isShared = shared.some(p => scene.resolveSceneObject(p, target) === child);
                return isShared ? parent : null;
            }
            child = parent;
            parent = child.getParent();
        }
        return null;
    }

    /**
     * The active state-edit context to nest new/imported assets under, or null.
     * This is the overlay's ACTIVE context (derived from the selection) so the
     * breadcrumb, the viewport frame, and asset-parenting all target the SAME
     * object (like Pages/Keyframes nest under the active container).
     *
     * Returns null — so parenting falls through to the scene root — unless the
     * context is REAL: it has Properties/states or was explicitly opened (marked).
     * A bare object that merely defaulted to itself (resolveContext step 3) must NOT
     * capture asset-parenting, or every add/import while States Editing is on would
     * nest under whatever plain mesh happens to be selected.
     */
    private getActiveStateRootForSelection(): SceneObject | null {
        return resolveStateParent(this.stateContext ?? null, {
            enabled: isStatesEditingEnabled(),
            selected: this.selectedObject,
            resolveContext,
        });
    }

    /**
     * The container that should capture an import right now — active page /
     * keyframes container / opened state context / Shared group — ensured to
     * exist (the Shared group and state-context rigs may need scaffolding).
     * Shared by every import path (file drop, catalog/studio panel, AI tools)
     * so they all land content in the same context. The returned commands must
     * be bundled with the import's create entry so one undo reverts wrap +
     * import together. `parent: null` means "leave it where the loader put it".
     */
    private async resolveImportContext(): Promise<{
        parent: SceneObject | null;
        commands: Array<UndoCommand | null>;
    }> {
        const stateRoot = this.getActiveStateRootForSelection();
        const sharedContainer = this.getSharedContextPagesContainer();
        let parent = resolveImportParent({
            activePage: this.getActivePageForSelection(),
            keyframeContainer: this.getActiveKeyframeContainerForSelection(),
            stateRoot,
        });
        let commands: Array<UndoCommand | null> = [];
        if (sharedContainer && this.viewer?.traceScene) {
            // Shared context: the import lands flat inside the "Shared" group.
            const ensured = await ensureSharedGroup(sharedContainer);
            if (ensured) {
                parent = ensured.group;
                commands = [...ensured.commands];
            }
        } else if (parent && parent === stateRoot) {
            // Lazy rigs: ensure the state context can parent the import (may
            // promote to / scaffold a rig).
            const ensured = await ensureStateContextContainer(parent);
            parent = ensured.container;
            commands = ensured.commands;
        }
        return { parent, commands };
    }

    /**
     * Adopt an asset imported outside loadFile (catalog/studio panel, resources
     * panel, AI tools — everything that dispatches `trace-catalog:import`):
     * honor a supplied parent or adopt into the active context, then record
     * undo (context scaffolding bundled) and select it.
     */
    private async adoptCatalogImport({ sceneObject, parent: explicitParent }: CatalogImportDetail): Promise<void> {
        const traceScene = this.viewer?.traceScene;
        if (!traceScene) return;
        const { parent, commands } = explicitParent
            ? { parent: explicitParent, commands: [] }
            : await this.resolveImportContext();
        if (parent) {
            traceScene.reparentSceneObject(sceneObject, parent);
        }
        const rootParent = sceneObject.getParent() ?? topLevelParent(traceScene);
        this.recordCreateForUndo(sceneObject, rootParent, [], commands);
        traceScene.select(sceneObject);
        this.selectedObject = sceneObject;
        this.updateDeleteButtonState();
        this.updateEmptySceneOverlay();
    }

    /**
     * Snapshot a SceneObject for undo and push a CreateObjectCommand.
     * `preCommands` (e.g. a lazy rig wrap from ensureStateContextContainer) are
     * bundled INTO the same undo entry so one Ctrl+Z reverts wrap + create.
     */
    private recordCreateForUndo(
        obj: SceneObject,
        parent: SceneObject | null,
        childEntries: CreateChildEntry[] = [],
        preCommands: Array<UndoCommand | null> = [],
    ): void {
        if (getUndoManager().isUndoRedoing) return;
        obj.captureTransform();
        obj.refreshSceneData();
        const createCmd = new CreateObjectCommand(
            obj,
            parent,
            obj.getSceneObjectData(),
            childEntries,
        );
        const real = preCommands.filter((c): c is UndoCommand => c !== null);
        getUndoManager().push(
            real.length > 0
                ? new CompositeCommand(`Create ${obj.name}`, [...real, createCmd])
                : createCmd,
        );
    }

    private async createNewObject(template: string = 'empty'): Promise<void> {
        if (!this.viewer?.traceScene) {
            console.error('[Sandbox] No scene available');
            return;
        }

        try {
            // If selection is inside a pages container, parent to the active page;
            // if inside a keyframes container, parent to that container so the new
            // object joins the keyframed subtree; if editing states, parent to the
            // state-root game object so it joins the edited group. Otherwise, root.
            const stateRoot = this.getActiveStateRootForSelection();
            const sharedContainer = this.getSharedContextPagesContainer();
            let parentObject: SceneObject | null = resolveNewObjectParent({
                activePage: this.getActivePageForSelection(),
                keyframeContainer: this.getActiveKeyframeContainerForSelection(),
                stateRoot,
                documentRoot: topLevelParent(this.viewer.traceScene),
            });
            // Lazy rigs: a rig-less state-context LEAF can't parent peer content —
            // ensure a container (may promote to / scaffold a rig) and bundle its
            // commands into the create's undo entry.
            let contextCommands: Array<UndoCommand | null> = [];
            if (sharedContainer) {
                // Shared context: content lands FLAT inside the container's
                // "Shared" group (created + registered lazily; its commands
                // bundle into the create's undo entry).
                const ensured = await ensureSharedGroup(sharedContainer);
                if (ensured) {
                    parentObject = ensured.group;
                    contextCommands = [...ensured.commands];
                }
            } else if (parentObject && parentObject === stateRoot) {
                const ensured = await ensureStateContextContainer(parentObject);
                parentObject = ensured.container;
                contextCommands = ensured.commands;
            }

            // Sandbox-specific templates — these have behavior presets, CDN assets,
            // or multi-object structures that don't belong in the shared core.
            if (template === 'particle-emitter') {
                const newObject = await this.viewer.traceScene.createSceneObject('ParticleEmitter', parentObject ?? undefined);
                console.log(`[Sandbox] Created new particle emitter: ${newObject.name}`);
                await this.addParticleSystemBehavior(newObject);
                this.recordCreateForUndo(newObject, parentObject, [], contextCommands);
                this.viewer.traceScene.select(newObject);
                this.selectedObject = newObject;
                this.updateDeleteButtonState();
                return;
            }

            if (template === 'pages') {
                const newObject = await this.viewer.traceScene.createSceneObject('Pages', parentObject ?? undefined);
                await newObject.addBehavior(Pages);
                const page1 = await this.viewer.traceScene.createSceneObject('Page 1', newObject);
                page1.transform.setEnabled(true);
                console.log(`[Sandbox] Created new pages container: ${newObject.name}`);
                page1.captureTransform();
                page1.refreshSceneData();
                this.recordCreateForUndo(newObject, parentObject, [{
                    object: page1,
                    data: page1.getSceneObjectData(),
                    parent: newObject,
                }], contextCommands);
                this.viewer.traceScene.select(newObject);
                this.selectedObject = newObject;
                this.updateDeleteButtonState();
                // A fresh Pages container is about to be authored — open its
                // strip so the pages context is immediately visible. The tab
                // opens before the mount publishes its adapter (that happens
                // on the selection update), so setOpen can't reach the
                // adapter's onToggleEnabled — set the capture mode directly.
                getStripTabs().setOpen('pages');
                setPageEditingEnabled(true);
                return;
            }

            if (template === 'timeline-widget') {
                const newObject = await createTimelineWidget(
                    this.viewer.traceScene,
                    parentObject,
                );
                console.log(`[Sandbox] Created new timeline widget: ${newObject.name}`);
                this.recordCreateForUndo(newObject, parentObject, [], contextCommands);
                this.viewer.traceScene.select(newObject);
                this.selectedObject = newObject;
                this.updateDeleteButtonState();
                return;
            }

            if (template === 'astronaut') {
                const { sceneObject: newObject } = await this.viewer.import(
                    'https://storageacceausprod.blob.core.windows.net/trace-cms-assets/639063468329689182_6951b5fa30754a0d95a4d0b62f65ff1f.glb',
                    'Astronaut'
                );
                // viewer.import mounts under the session's document root —
                // move it under the resolved context parent (shared group /
                // page / state root) like every other template.
                if (parentObject) {
                    this.viewer.traceScene.reparentSceneObject(newObject, parentObject);
                }
                // Offset so the bottom rests at y=0
                const { min } = newObject.transform.getHierarchyBoundingVectors(true);
                newObject.transform.position.y -= min.y;
                if (!newObject.getBehavior(Renderer)) {
                    await newObject.addBehavior(Renderer);
                }
                console.log(`[Sandbox] Created new astronaut: ${newObject.name}`);
                this.recordCreateForUndo(newObject, parentObject, [], contextCommands);
                this.viewer.traceScene.select(newObject);
                this.selectedObject = newObject;
                this.updateDeleteButtonState();
                return;
            }

            // Shared templates: empty / cube / sphere / plane / cylinder / text.
            // Pre-validated against the shared registry — any unknown template here
            // is a programming error in the caller, not user input.
            const newObject = await createFromTemplate(
                this.viewer.traceScene,
                template as ObjectTemplate,
                { parent: parentObject },
            );
            console.log(`[Sandbox] Created new object: ${newObject.name}${parentObject ? ` (under ${parentObject.name})` : ''}`);
            this.recordCreateForUndo(newObject, parentObject, [], contextCommands);
            this.viewer.traceScene.select(newObject);
            this.selectedObject = newObject;
            this.updateDeleteButtonState();
        } catch (error) {
            console.error('[Sandbox] Failed to create object:', error);
        } finally {
            this.updateEmptySceneOverlay();
        }
    }

    /**
     * Add a ParticleSystem behavior to a SceneObject.
     * Schema defaults cover required fields; we add optional presets for a nice out-of-box look.
     */
    private async addParticleSystemBehavior(sceneObject: SceneObject): Promise<void> {
        await sceneObject.addBehavior(ParticleSystem, {
            startColor2: '#FF6600FF',
            endColor: '#00000000',
            particleScale: 0.25,
            sizeOverLifetime: [
                { time: 0, value: 0.5 },
                { time: 0.3, value: 1 },
                { time: 1, value: 0 },
            ],
        });
        console.log(`[Sandbox] Added ParticleSystem behavior to ${sceneObject.name}`);
    }

    private toggleSelectedObjectActive(): void {
        if (!this.selectedObject) {
            console.warn('[Sandbox] No object selected');
            return;
        }

        const obj = this.selectedObject;
        const after = !(obj.transform?.isEnabled() ?? obj.active);
        void toggleActive(obj, after).then((where) => console.log(where === 'keyframe'
            ? `[Sandbox] ${obj.name} active=${after} captured into the selected keyframe`
            : `[Sandbox] ${obj.name} active=${after} asked; shows ${obj.transform?.isEnabled() ?? obj.active}`));
    }

    /**
     * Apply a new primary selected object: update tracked state, delete-button
     * state, context overlays, and close the clip editor. Called by the
     * multi-selection model's authoritative primary callback (or the raw
     * selection-changed event before the model exists).
     */
    private applySelectedObject(primary: SceneObject | null): void {
        this.selectedObject = primary;
        this.updateDeleteButtonState();

        // Update context overlays
        this.pageContext?.update(this.selectedObject);
        this.keyframeContext?.update(this.selectedObject);
        this.stateContext?.update(this.selectedObject);

        // Close clip editor when selection changes (user must re-open via "Create Clip")
        if (this.clipEditor?.isActive()) {
            this.clipEditor.close();
        }
    }

    private deleteSelectedObject(): void {
        if (!this.viewer?.traceScene) {
            console.error('[Sandbox] No scene');
            return;
        }

        // Gather the full selection set, unioned with the single primary. The
        // model is the source of truth for multi-select, but selection can also
        // arrive via paths that only touch `selectedObject` (programmatic select,
        // inspector-tree click), so include it to never lose the primary.
        const selectedUnion = new Set<SceneObject>(this.multiSelection?.getSelectedObjects() ?? []);
        if (this.selectedObject) selectedUnion.add(this.selectedObject);

        // Neither root is deletable — the engine root is not a node and the
        // document root IS the document (D8) — and a refusal says so rather
        // than silently dropping it. Then prune to top-most objects (deleting
        // an ancestor already removes its selected descendants — one shared
        // helper with the group-transform prune to avoid the two drifting).
        const deletable: SceneObject[] = [];
        for (const obj of selectedUnion) {
            const refusal = structuralRootRefusal(obj, 'delete');
            if (refusal) console.warn(`[Sandbox] ${refusal}`);
            else deletable.push(obj);
        }
        const targets = topMostSelected(deletable);

        if (targets.length === 0) {
            console.warn('[Sandbox] Nothing deletable in selection');
            return;
        }

        // Deleting a node that belongs to a placed reference is an edit inside
        // it (SCENE_ROOT_SPEC §2.3 "Editing"): copy the asset into this scene
        // first, then delete the scene's own node. The targets keep their
        // identity across the copy, so the second pass deletes exactly what was
        // selected — and the check is synchronous, so the ordinary delete stays
        // synchronous too.
        if (targets.some(needsReferenceCopy)) {
            const pending = targets;
            void copyForInsideEdit(pending).then(copied => {
                if (copied) this.deleteObjects(pending);
            });
            return;
        }

        this.deleteObjects(targets);
    }

    /**
     * Delete a resolved set of top-most targets: page children through
     * `deletePage`, everything else through `deleteSceneObject`, all of it in
     * one undo entry.
     */
    private deleteObjects(selected: readonly SceneObject[]): void {
        if (!this.viewer?.traceScene) {
            console.error('[Sandbox] No scene');
            return;
        }
        const traceScene = this.viewer.traceScene;
        let targets: SceneObject[] = [...selected];

        // Direct PAGE children route through deletePage: it splices the
        // positional pages[] entry (a raw delete would silently misalign
        // every later page's assignments), keeps the strip selection alive,
        // and pushes the full undo bundle (object + data + order restore).
        // Sequential await because deleting shifts the sibling indices.
        const pageTargets: Array<{ obj: SceneObject; container: SceneObject }> = [];
        const rest: SceneObject[] = [];
        for (const t of targets) {
            const parent = t.getParent();
            if (parent?.getBehavior(Pages) && pageChildrenOf(parent).includes(t)) {
                pageTargets.push({ obj: t, container: parent });
            } else {
                rest.push(t);
            }
        }
        if (pageTargets.length > 0) {
            this.multiSelection?.clear();
            void (async () => {
                for (const pt of pageTargets) {
                    const idx = pageChildrenOf(pt.container).indexOf(pt.obj);
                    if (idx >= 0) pushOp(await deletePage(pt.container, idx));
                }
            })();
        }
        if (rest.length === 0) {
            this.updateDeleteButtonState();
            this.updateEmptySceneOverlay();
            return;
        }
        targets = rest;

        // Build each command while the object is still live, then delete it.
        // Only objects that actually delete contribute to the undo command — a
        // command for a still-live object would re-add a duplicate on undo.
        const commands: UndoCommand[] = [];
        const survivors: SceneObject[] = [];
        let deletedCount = 0; // user-facing count: selected targets only, not pruned rigs
        let reselectParent: SceneObject | null = null;
        for (const t of targets) {
            const cmd = buildDeleteCommand(t); // must capture before deleting
            const parent = t.getParent() ?? null;   // before delete — unreliable after
            if (traceScene.deleteSceneObject(t)) {
                commands.push(cmd);
                deletedCount++;
                reselectParent = parent;
                // LAZY-RIG cleanup: deleting the sole wrapped child would strand a
                // stateful-but-empty "<name> Rig". Prune it into the SAME undo
                // entry so one Ctrl+Z restores child + rig, re-nested.
                const prune = pruneEmptyRig(parent);
                if (prune) commands.push(prune);
            } else {
                survivors.push(t);
                console.error(`[Sandbox] Failed to delete object: ${t.name}`);
            }
        }

        if (commands.length === 0) {
            // Every delete failed — preserve the selection so the user sees what
            // is still selected and can retry, rather than silently deselecting.
            console.error('[Sandbox] No objects were deleted');
            return;
        }

        if (!getUndoManager().isUndoRedoing) {
            const command: UndoCommand = commands.length === 1
                ? commands[0]
                : new CompositeCommand(
                    // Label by what the USER deleted — an auto-pruned rig rides
                    // along in the bundle but shouldn't inflate the entry name.
                    deletedCount === 1 ? commands[0].label : `Delete ${deletedCount} objects`,
                    commands,
                );
            getUndoManager().push(command);
        }

        console.log(`[Sandbox] Deleted ${deletedCount} object(s)`);
        // Reselect any objects that failed to delete (partial failure) rather than
        // clearing everything; otherwise land on the deleted object's live
        // parent — a cleared selection closes every selection-anchored context
        // UI (pages/states strips). A top-level delete lands on the DOCUMENT
        // ROOT — under D8 that is the file's own node, which is what "nothing
        // selected" means; only the engine mount point is never selected.
        this.multiSelection?.clear();
        if (survivors.length > 0) {
            const model = this.multiSelection?.getModel();
            survivors.forEach((s, i) => (i === 0 ? model?.replace(s) : model?.add(s)));
            this.selectedObject = this.multiSelection?.getPrimary() ?? survivors[survivors.length - 1];
        } else {
            const live = reselectParent
                && !reselectParent.transform.isDisposed()
                && !isEngineRoot(reselectParent)
                ? reselectParent : null;
            traceScene.select(live);
            this.selectedObject = live;
        }
        this.updateDeleteButtonState();
        this.updateEmptySceneOverlay();
    }

    /**
     * Duplicate the currently selected object and all its descendants, as a
     * sibling with a "(1)", "(2)", … suffix (trace-viewer's `duplicateObjects`).
     */
    public async duplicateSelectedObject(): Promise<void> {
        if (!this.selectedObject || !this.viewer?.traceScene) {
            console.error('[Sandbox] No object selected');
            return;
        }

        try {
            const traceScene = this.viewer.traceScene;
            // Duplicate exactly what's selected — no rig hoisting. To copy a whole
            // stateful rig, select the rig node (visible in the outliner) and
            // duplicate that. Copying a member inside a rig yields a sibling member
            // of the same group (its member states are rig-relative).
            const sourceObject = this.selectedObject;
            const result = await duplicateObjects(sourceObject);
            if ('refused' in result) {
                console.warn(`[Sandbox] Not duplicated: ${result.refused.message}`);
                return;
            }
            getUndoManager().push(result.command);
            const duplicateObject = result.copies.get(sourceObject)!;

            // Select the newly created duplicate
            traceScene.select(duplicateObject);
            this.selectedObject = duplicateObject;
            this.updateDeleteButtonState();
            console.log(`[Sandbox] Duplicated object: ${sourceObject.name} -> ${duplicateObject.name} (${sourceObject.getDescendants().length} descendants)`);
        } catch (error) {
            console.error('[Sandbox] Failed to duplicate object:', error);
        }
    }

    private updateDeleteButtonState(): void {
        // Neither root is deletable: the engine root is not a node, and the
        // document root IS the document (D8).
        this.deleteObjectBtn.disabled = !this.selectedObject || isStructuralRoot(this.selectedObject);
    }

    private async exportSceneAsGlz(): Promise<void> {
        if (!this.viewer) {
            console.error('[Sandbox] No scene to export');
            return;
        }

        console.log('[Sandbox] Exporting scene as .glz...');
        const blob = await exportGlz(this.viewer);
        downloadBlob(blob, bundleFileName(this.viewer.traceScene?.sceneName));
        console.log('[Sandbox] Scene .glz export complete');
    }

    private async exportSceneAsGltf(): Promise<void> {
        if (!this.viewer) {
            console.error('[Sandbox] No scene to export');
            return;
        }

        console.log('[Sandbox] Exporting scene as .gltf...');
        try {
            const blob = await this.viewer.exportSceneToGltfBlob(editorExclusion(this.viewer.traceScene));
            downloadBlob(blob, `${this.viewer.traceScene?.sceneName ?? 'scene'}.gltf`);
            console.log('[Sandbox] Scene .gltf export complete');
        } catch (err) {
            console.error('[Sandbox] .gltf export failed:', err);
            const msg = err instanceof Error ? err.message : String(err);
            showToast(`glTF export failed: ${msg}`, 'error');
        }
    }

    private _cmsStatusDot: HTMLElement | null = null;
    private _unsubCmsPublish: (() => void) | null = null;

    private setupCmsStatusDot(): void {
        this._cmsStatusDot = document.getElementById('cms-status-dot');
        if (!this._cmsStatusDot) return;

        this.updateCmsStatusDot();
        this._unsubCmsPublish = getUndoManager().subscribe((depthShifted) => {
            if (depthShifted) this._editorSession.onUndoDepthShifted();
            this.updateCmsStatusDot();
        });
    }

    /** Fully disconnect from publish tracking (CMS or asset). */
    private disconnectCmsPublish(): void {
        this.cmsManager = null;
        setExportCmsManager(null);
        setActiveCmsManager(null);
        this._editorSession.disconnect();
        clearSourceAsset();
        resetAssetFolderSession();

        // Unsubscribe from undo-depth tracking
        this._unsubCmsPublish?.();
        this._unsubCmsPublish = null;

        // Hide publish UI
        if (this._cmsStatusDot) {
            this._cmsStatusDot.className = '';
            this._cmsStatusDot = null;
        }
        this.forEachPublishButton(btn => {
            btn.style.display = 'none';
            btn.disabled = false;
        });
    }

    /**
     * Publish menu items. The dropdown surfaces both `.zip` and `.gltf` for
     * CMS scenes while the new format is experimental; in asset-republish
     * mode the `.zip` button is reused with a generic "Publish" label and
     * the `.gltf` one is hidden.
     *
     * `cmsLabel` is the canonical label for CMS-scene mode. Reapplied on
     * every call to {@link showPublishButtons} so a previous switch to
     * asset mode (which writes "Publish" into the zip button) never bleeds
     * into the next CMS-scene load.
     *
     * The label lives in a `<span class="publish-label">` inside each button
     * (see index.html). We target the span directly rather than relying on
     * the DOM order of sibling text nodes, so adding wrappers/icons later
     * won't silently break label switching.
     */
    private static readonly PUBLISH_BUTTONS: ReadonlyArray<{ id: string; cmsLabel: string; format: RootContentFormat }> = [
        { id: 'export-publish-zip', cmsLabel: 'Publish as .zip', format: 'zip' },
        { id: 'export-publish-gltf', cmsLabel: 'Publish as .gltf', format: 'gltf' },
    ];

    /** Run a callback against every publish button currently in the DOM. */
    private forEachPublishButton(fn: (btn: HTMLButtonElement, format: RootContentFormat) => void): void {
        for (const { id, format } of TraceSandbox.PUBLISH_BUTTONS) {
            const btn = document.getElementById(id) as HTMLButtonElement | null;
            if (btn) fn(btn, format);
        }
    }

    /**
     * Show the publish buttons appropriate to the current republish mode and
     * re-apply their canonical labels (so switching asset → cms-scene
     * doesn't leave the zip button reading "Publish" instead of
     * "Publish as .zip").
     *
     * - `'cms-scene'`: both `.zip` and `.gltf` are shown, each with its
     *   format-specific label.
     * - `'asset'`: only the `.zip` button is shown, relabelled to a
     *   generic "Publish". Asset republish goes through PATCH `/assets/{id}`
     *   with a fixed `.trace` payload — there's no format choice on this
     *   path, so a format-suffixed label would be misleading.
     */
    private showPublishButtons(mode: 'cms-scene' | 'asset'): void {
        for (const { id, cmsLabel } of TraceSandbox.PUBLISH_BUTTONS) {
            const btn = document.getElementById(id) as HTMLButtonElement | null;
            if (!btn) continue;
            const label = btn.querySelector('.publish-label');
            if (mode === 'cms-scene') {
                btn.style.display = '';
                if (label) label.textContent = cmsLabel;
            } else if (id === 'export-publish-zip') {
                btn.style.display = '';
                if (label) label.textContent = 'Publish';
            } else {
                btn.style.display = 'none';
            }
        }
    }

    private updateCmsStatusDot(): void {
        if (!this._cmsStatusDot) return;
        const isDirty = this._editorSession.isDirty;
        this._cmsStatusDot.className = isDirty ? 'dirty' : 'current';

        // Disable publish when already in-sync or mid-publish. A clean
        // session can still have an actionable publish, though: re-encoding
        // the stored Root in the other format (zip ↔ gltf switch) — keep
        // that button enabled or the switch is an unclickable no-op.
        this.forEachPublishButton((btn, format) => {
            const formatSwitch = this.cmsManager !== null && this.cmsManager.lastRootFormat !== format;
            btn.disabled = (!isDirty && !formatSwitch) || this._editorSession.isPublishing;
        });
    }

    private async publishScene(options: PublishSceneOptions = {}): Promise<void> {
        if (!this.viewer) {
            console.error('[Sandbox] Cannot publish: no viewer');
            return;
        }

        // CMS scenes publish through CmsManager — omnibus over the scene
        // document and the image-anchor accessor
        if (this.cmsManager) {
            if (!this._editorSession.beginPublish()) return;
            this.updateCmsStatusDot();
            const dismiss = showSpinnerToast('Publishing...');

            try {
                const result = await this.cmsManager.publishScene(this.viewer, options);
                const anyPublished = result.rootPublished;
                const allClean = !anyPublished && result.failures.length === 0;

                if (allClean) {
                    // Snapshot says we're clean; reconcile undo-depth dirty bit so
                    // the dirty indicator stops lingering after edit→undo→publish.
                    this._editorSession.markPublished();
                    dismiss('Already up to date', 'success');
                } else if (result.failures.length === 0) {
                    // All-success path — mark clean against current undo depth
                    this._editorSession.markPublished();
                    dismiss(buildPublishSummary(result), 'success');
                } else if (anyPublished) {
                    // Partial success — leave dirty so user can retry
                    dismiss(buildPublishSummary(result), 'error');
                } else {
                    // Total failure
                    dismiss(`Publish failed: ${result.failures.join('; ')}`, 'error');
                }
            } catch (error) {
                console.error('[Sandbox] Publish failed:', error);
                const msg = error instanceof Error ? error.message : String(error);
                dismiss(`Publish failed: ${msg}`, 'error');
            } finally {
                this._editorSession.endPublish();
                this.updateCmsStatusDot();
            }
            return;
        }

        // Catalog assets publish through the Assets API (.trace format)
        if (this._editorSession.sourceAsset) {
            return this.publishAsset();
        }

        console.error('[Sandbox] Cannot publish: no CMS connection or source asset');
    }

    /**
     * Persist the current scene for the headless `save_scene` tool. Unlike
     * `publishScene`, this RESOLVES with a short summary (or REJECTS on failure)
     * instead of only toasting — the agent needs to know the outcome. Routes the
     * same way the UI does: CMS publish → source-asset PATCH → file download
     * fallback (when the scene isn't connected to any persistent store).
     */
    async saveForAgent(): Promise<string> {
        if (!this.viewer) throw new Error('No scene loaded.');

        if (this.cmsManager || this._editorSession.sourceAsset) {
            const wasDirty = this._editorSession.isDirty;
            await this.publishScene();
            // publishScene marks the session clean on success; if it's still
            // dirty, the publish did not go through.
            if (this._editorSession.isDirty && wasDirty) {
                throw new Error('Save did not complete — see console/toast for the publish error.');
            }
            return this.cmsManager
                ? 'Published scene to CMS.'
                : 'Saved scene back to its source asset.';
        }

        // Not connected to any persistent store — fall back to a .glz download (the
        // scene download format) so edits aren't lost, and say so plainly.
        await this.exportSceneAsGlz();
        return 'This scene is not connected to a CMS scene or catalog asset, so it ' +
            'cannot be saved in place. Downloaded a .glz file instead — open or ' +
            'import it to continue from these edits.';
    }

    /**
     * Begin (or refresh) the folder session for the asset the editor just
     * opened, so imports and publishes have somewhere to put bytes.
     *
     * Needs a token because the session owns an authenticated client to
     * `POST /assets/{id}/files`. Without one there is no session and the
     * editor behaves as it did before the folder model: imports stay in
     * memory, and the publish path opens a session of its own once it has
     * checked for a token.
     */
    private openAssetFolder(asset: SourceAsset): void {
        const token = getCatalogToken();
        if (!token || !asset.assetId) return;
        openAssetFolderSession({
            assetId: asset.assetId,
            teamId: asset.teamId,
            rowUrl: asset.url,
            blobBaseUrl: BLOB_BASE_URL,
            client: new Assets(apiConfig(token)),
        });
    }

    /**
     * Save the current scene back to the library asset it was opened from
     * (CONTENT_MODEL_SPEC §2.6 "Save as asset", §3.1).
     *
     * An asset is a folder, so this is the same write a scene save is — and
     * the same **two** writes (`publishAssetFolder`, `src/cms/asset-folder.ts`):
     * the files the folder does not already hold go to
     * `POST /assets/{id}/files`, then the glTF 2.1 document goes alone in the
     * PATCH. **No ZIP** — editing a library asset used to repack every byte it
     * owned to change one number; and no bytes in the PATCH either, which is
     * the rule the whole layout exists for: *nothing re-uploads a binary the
     * folder already holds* (§2.2).
     *
     * It has to be two writes because **the server mints storage names and
     * the client never does** (§2.1). The exporter authors `assets/cylinder.glb`
     * from the file's own name; the folder stores it as
     * `assets/cylinder.<hash16>.glb`. `PATCH /assets/{id}` with a document
     * validates every relative `files[k].uri` against what the folder actually
     * holds and answers **400** on a mismatch — it never rewrites the client's
     * document, because the stored bytes must stay the client's bytes and the
     * document hash the client's hash. So the names have to come back from
     * `/files` *before* the document names them, and the repoint covers
     * `files[]` and the behavior paths naming those files
     * (trace-viewer `uploadFolder`), never a re-export: the `externalAssets` and
     * `extras.trace.file` indices beside it are positional and must not move.
     * A refused PATCH is retried once with the session's records forgotten
     * (`saveFolder`, `src/cms/scene-folder.ts`).
     *
     * Sending both in one multipart — what this used to do — could only work
     * when nothing new travelled at all.
     *
     * The export runs with the closure rule on (`rejectSceneFolderUrls`,
     * §3.2): an asset may reference other library assets and foreign
     * streamable media, but never a scene folder's files — those belong to
     * that scene and die with it.
     *
     * Nothing sends `Metadata` any more. Root behaviors have one home, the
     * document's `scenes[0].extras.trace` (§3.6), and the server strips the
     * column on write — so the old "metadata-only republish" branch has
     * nothing left to send and is gone with it.
     */
    private async publishAsset(): Promise<void> {
        const sourceAsset = this._editorSession.sourceAsset;
        if (!this.viewer || !sourceAsset?.assetId) return;

        if (!this._editorSession.beginPublish()) return;
        this.updateCmsStatusDot();
        const dismiss = showSpinnerToast('Publishing...');

        try {
            const token = getCatalogToken();
            if (!token) throw new Error('Not authenticated');

            const traceScene = this.viewer.traceScene;
            if (!traceScene) throw new Error('Scene not initialized');

            // A document that was never named carries the placeholder root
            // name; the asset has a title, so the file's own root takes it —
            // that name is what every placement of this asset shows in the
            // tree. Before the comparison below, so the export, the tree and
            // the new clean baseline all agree (D8).
            renameDefaultDocumentRoot(traceScene, sourceAsset.title);

            // The same snapshot the open took (`assetContentSnapshot`): the
            // DOCUMENT ROOT is in the comparison, because its behaviors are
            // the document's own and go into what this publish writes (D8).
            const currentExportData = assetExportData(traceScene);
            const decision = resolvePublishDecision(
                currentExportData, this._editorSession.contentSnapshot,
            );

            if (!decision.contentChanged) {
                this._editorSession.markPublished();
                dismiss('Already up to date', 'success');
                return;
            }

            const assetsClient = new Assets(apiConfig(token));
            const session = openAssetFolderSession({
                assetId: sourceAsset.assetId,
                teamId: sourceAsset.teamId,
                rowUrl: getSourceAsset()?.url,
                blobBaseUrl: BLOB_BASE_URL,
                client: assetsClient,
            });

            const scene = this.viewer.scene;
            const cam = scene?.activeCamera;
            let thumbnail: File | undefined;
            if (scene && cam instanceof ArcRotateCamera) {
                thumbnail = await captureSceneThumbnail(scene, cam);
            }

            // The asset is a folder: the document plus the files it names.
            //
            // `destination: 'same'` with the asset's `folderUrl`: this PATCHes
            // the asset the scene was OPENED from, so the files the folder
            // already holds (streamed from it, imported into it, or stored by
            // an earlier save of this session with the same bytes) come back
            // on `existing` and are not re-sent. `'other'` belongs to a write
            // into a *different* folder (a promotion, spec §3.5).
            //
            // Absent parts are simply absent: the generated `createFormData`
            // omits a key whose value is `undefined` or `null` (patched in
            // trace-viewer #811 — it used to append the literal text
            // "undefined" as the part's body). So a scene with no thumbnail
            // sends no `Thumbnail` part, without a wrapper at the call, and
            // there is never a `Files` part at all. `test/asset-publish.test.ts`
            // pins that against the real builder.
            const viewer = this.viewer;
            await publishAssetFolder(
                {
                    uploader: session.uploader,
                    patch: (assetId, parts) => assetsClient.assetsPartialUpdate(assetId, parts),
                    stored: { folderUrl: session.baseUrl, resources: traceScene.resources },
                },
                () => viewer.exportSceneFolder({
                    manifest: assetManifestOptions(API_BASE_URL, BLOB_BASE_URL),
                    ...editorExclusion(viewer.traceScene),
                    destination: 'same',
                    folderUrl: session.baseUrl,
                }),
                {
                    assetId: sourceAsset.assetId,
                    teamId: sourceAsset.teamId,
                    documentName: `${sourceAsset.title ?? 'asset'}.gltf`,
                    thumbnail,
                },
            );

            this._editorSession.markPublished(snapshotExportData(currentExportData));
            dismiss('Asset updated', 'success');
        } catch (error) {
            console.error('[Sandbox] Asset publish failed:', error);
            const responseData = (error as Error & { response?: { data?: unknown } })?.response?.data;
            if (responseData) console.error('[Sandbox] API response:', responseData);
            const msg = error instanceof Error ? error.message : String(error);
            dismiss(`Publish failed: ${msg}`, 'error');
        } finally {
            this._editorSession.endPublish();
            this.updateCmsStatusDot();
        }
    }

    private setupDragAndDrop(): void {
        // Use document-level listeners to catch all drag events, then filter by target
        document.addEventListener('dragover', (e) => this.onDragOver(e));
        document.addEventListener('drop', (e) => void this.onDrop(e));
        document.addEventListener('dragenter', (e) => this.onDragEnter(e));
        document.addEventListener('dragleave', (e) => this.onDragLeave(e));

        // Click overlay to open file picker
        this.emptySceneOverlay.querySelector('.empty-scene-content')!.addEventListener('click', () => {
            this.pickFiles('import');
        });

        this.filePicker.addEventListener('change', () => {
            const files = this.filePicker.files;
            if (!files || files.length === 0) return;
            const mode = this.filePickerMode;
            const selected = Array.from(files);
            void (async () => {
                // Companion files (.bin/textures/GLBs referenced by a selected
                // .gltf/.glxf) register as sibling assets, not standalone loads.
                const { loadables, siblings } = await partitionSiblingFiles(selected);
                if (mode === 'open') {
                    // FileList order is arbitrary — pick the file that's
                    // actually a scene, not whatever sorts first (a stray
                    // unreferenced texture must not win over the .gltf).
                    const primary = TraceSandbox.pickPrimaryLoadable(loadables);
                    let openSiblings = siblings;
                    if (primary && loadables.length > 1) {
                        console.warn(`[Sandbox] Open loads a single scene — ignoring: ${loadables.filter(f => f !== primary).map(f => f.name).join(', ')}`);
                        // Re-partition against just the opened file so
                        // companions of the ignored scene files don't get
                        // registered into the opened scene.
                        const companions = selected.filter(f => f === primary || !loadables.includes(f));
                        ({ siblings: openSiblings } = await partitionSiblingFiles(companions));
                    }
                    if (primary) {
                        await this.openSceneFile(primary, openSiblings);
                    }
                } else {
                    this.registerSiblings(siblings);
                    for (const file of loadables) {
                        await this.loadFile(file);
                    }
                }
                this.filePicker.value = '';
            })();
        });
    }

    /** Choose which of a multi-file selection to open: scene formats beat
     *  single-asset models beat everything else; selection order breaks ties
     *  (Array.prototype.sort is stable). */
    private static pickPrimaryLoadable(loadables: File[]): File | undefined {
        const rank = (f: File): number => {
            const ext = f.name.toLowerCase().split('.').pop() ?? '';
            if (EDITOR_SCENE_EXTENSIONS.has(ext)) return 0;
            if (ext === 'gltf' || ext === 'glb') return 1;
            return 2;
        };
        return [...loadables].sort((a, b) => rank(a) - rank(b))[0];
    }

    /** Register companion files for an additive import; the viewer throws if
     *  the scene isn't initialized yet (e.g. WebGL init failed), which would
     *  otherwise become an unhandled rejection in the picker/drop handlers. */
    private registerSiblings(siblings: Map<string, Blob>): void {
        if (!siblings.size) return;
        if (this.viewer?.traceScene) {
            this.viewer.registerExternalAssets(siblings);
        } else {
            console.warn('[Sandbox] Scene not ready — companion files not registered; external refs may not resolve');
        }
    }

    /** Show the file picker ('import' adds to the current scene, 'open' replaces it).
     *  Multi-select is allowed in both modes so a .gltf/.glxf can be picked
     *  together with the external files it references. */
    private pickFiles(mode: 'import' | 'open'): void {
        this.filePickerMode = mode;
        this.filePicker.multiple = true;
        this.filePicker.click();
    }

    private _dragCounter = 0;

    private setDragVisuals(active: boolean): void {
        this.viewerWrapper.style.opacity = active ? '0.7' : '1';
        this.viewerWrapper.style.outline = active ? '3px dashed #4CAF50' : 'none';
        // Enable pointer events on the overlay during drag so it can receive drop events
        this.emptySceneOverlay.style.pointerEvents = active ? 'auto' : '';
    }

    /** Check if drag target is the viewer area (not inspector panels) */
    private isOverViewerCanvas(e: DragEvent): boolean {
        const target = e.target as HTMLElement;
        if (!target) return false;
        if (target.closest('#babylon-inspector-container')) return false;
        return target.closest('.viewer-container') !== null;
    }

    private onDragOver(e: DragEvent): void {
        if (!this.isOverViewerCanvas(e)) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }

    private onDragEnter(e: DragEvent): void {
        if (!this.isOverViewerCanvas(e)) return;
        e.preventDefault();
        this._dragCounter++;
        if (this._dragCounter === 1) this.setDragVisuals(true);
    }

    private onDragLeave(e: DragEvent): void {
        if (!this.isOverViewerCanvas(e)) return;
        e.preventDefault();
        this._dragCounter--;
        if (this._dragCounter <= 0) {
            this._dragCounter = 0;
            this.setDragVisuals(false);
        }
    }

    private async onDrop(e: DragEvent): Promise<void> {
        this._dragCounter = 0;
        this.setDragVisuals(false);
        if (!this.isOverViewerCanvas(e)) return;
        e.preventDefault();

        if (!e.dataTransfer?.files.length) {
            return;
        }

        await this.importFiles(Array.from(e.dataTransfer.files));
    }

    /**
     * Everything a canvas drop does with its files: companions register as
     * siblings, then each supported file is imported in turn. The AI assistant
     * imports the files the user hands it through here too (ai/scene-files.ts).
     */
    private async importFiles(files: File[]): Promise<ImportedFile[]> {
        // Companion files referenced by a dropped .gltf/.glxf (.bin buffers,
        // textures, GLBs) register as sibling assets, not standalone loads —
        // partition BEFORE the supported-type filter so e.g. .bin survives.
        const { loadables, siblings } = await partitionSiblingFiles(files);

        const validFiles = loadables.filter(file => {
            const ext = file.name.toLowerCase().split('.').pop() ?? '';
            return DROPPABLE_ASSET_EXTENSIONS.has(ext) || EDITOR_SCENE_EXTENSIONS.has(ext);
        });

        if (validFiles.length === 0) {
            console.warn(`[Sandbox] No supported files. Supported: ${[...DROPPABLE_ASSET_EXTENSIONS, ...EDITOR_SCENE_EXTENSIONS].join(', ')}`);
            return [];
        }

        this.registerSiblings(siblings);

        const imported: ImportedFile[] = [];
        for (const file of validFiles) {
            imported.push(await this.loadFile(file));
        }
        return imported;
    }

    /**
     * Open a file as a new scene, replacing the current content — the local-file
     * counterpart of the catalog's doOpenAsset. Unlike loadFile, which imports
     * additively into the existing scene.
     */
    private async openSceneFile(file: File, siblings?: Map<string, Blob>): Promise<void> {
        if (sceneHasContent() && !window.confirm(`Replace the current scene with "${file.name}"? Unsaved changes will be lost.`)) {
            return;
        }

        if (!this.viewer) {
            await this.createEmptyViewer();
        }

        console.log(`[Sandbox] Opening: ${file.name}`);
        // Destroy grid before loading (recreated in onSceneLoaded if enabled)
        this.destroyGrid();

        try {
            await this.viewer!.openBlob(file, file.name, { siblings });
            // Every session has a document root (D8). A document brings its
            // own; a legacy flat archive is adopted here, named for the file.
            await adoptDocumentRoot(this.viewer!.traceScene, fileNameStem(file.name));
            await restoreEditorContent(this.viewer!.traceScene, file, true);
        } catch (error) {
            console.error(`[Sandbox] Failed to open ${file.name}:`, error);
        } finally {
            // openBlob clears the old scene before loading, so even on failure
            // the scene changed — rewire editor state (undo, CMS, grid, overlay).
            this.onSceneLoaded();
        }
    }

    /**
     * The canvas drop of one file (drop-import.ts), then the editor's own
     * state: selection, panels and the empty-scene overlay. Never throws.
     */
    private async loadFile(file: File): Promise<ImportedFile> {
        this.emptySceneOverlay.classList.remove('visible');
        console.log(`[Sandbox] Loading: ${file.name}`);
        const traceScene = this.viewer?.traceScene;
        if (!traceScene) {
            console.error('[Sandbox] No viewer available');
            return { name: file.name, objects: [], error: 'no scene is open' };
        }
        try {
            const objects = await importDroppedFile(traceScene, file, () => this.resolveImportContext());
            console.log(`[Sandbox] Loaded: ${file.name}`);
            const first = objects[0];
            if (first) {
                traceScene.select(first);
                this.selectedObject = first;
                this.updateDeleteButtonState();
                // Force behavior panel refresh — the selection event may have
                // fired before async behavior setup (Renderer add, etc.) finished.
                // behaviorType '*' = wildcard; panel listeners match on objectPath only.
                dispatchBehaviorChanged({ objectPath: first.getPath(), behaviorType: '*', action: 'added' });
            }

            // Last, once the import's own objects are placed and recorded for
            // undo: the editor folder is not part of what was imported.
            await restoreEditorContent(traceScene, file, false);
            dispatchResourcesChanged({ action: 'added', path: null });
            return { name: file.name, objects };
        } catch (error) {
            console.error(`[Sandbox] Failed to load ${file.name}:`, error);
            return { name: file.name, objects: [], error: error instanceof Error ? error.message : String(error) };
        } finally {
            this.updateEmptySceneOverlay();
        }
    }
}

// Declare this deployment's hosted font packs, like Studio's boot. Only URLs
// are registered, and nothing waits on it: a slow or failed manifest leaves
// the bundled fonts drawing (src/host-font-packs.ts).
void initHostFontPacks();

// Initialize when page loads
document.addEventListener('DOMContentLoaded', () => {
    new TraceSandbox();
});

if (import.meta.hot) {
    // Self-accept so edits to the imperative core don't trigger Vite's
    // full-page-reload fallback, which would clear the loaded scene. The
    // trade-off: the running TraceSandbox instance keeps executing the OLD
    // module graph — the updated code is fetched but not live. Surface that
    // instead of leaving it silent, so "my change did nothing" is answered by
    // a toast rather than a debugging session. (React components and the
    // plugin/tool registries do hot-apply via their own boundaries; this
    // callback only fires for updates that bubbled all the way up here.)
    import.meta.hot.accept(() => {
        console.warn('[Sandbox] Core module hot-updated, but the running editor still uses the old code — refresh to apply (the scene will reload).');
        showToast('Editor core changed — refresh to apply', 'info', 6000);
    });
}
